// src/components/shopping/JewelleryStudio.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";

/* ============================================================
   TYPES
   ============================================================ */
export interface StudioDesignPayload {
  piece: "necklace" | "bracelet" | "anklet";
  length: number;
  string: string;
  stringColor: string;
  beads: Array<{
    id: string;
    name: string;
    kind: string;
    size: number;
    price: number;
    hex?: string;
    metal?: string;
    shape?: string;
    uid: string;
  }>;
  totalPrice: number;
  previewDataURL: string;
  metalType?: string;
  gemstone?: string;
}

interface JewelleryStudioProps {
  initialPiece?: "necklace" | "bracelet" | "anklet";
  onSave?: (design: StudioDesignPayload) => void;
  onAddToCart?: (design: StudioDesignPayload) => void;
  onBuyNow?: (design: StudioDesignPayload) => void;
}

/* ============================================================
   SCOPED CSS — Scoop & Stone themed
   ============================================================ */
const STUDIO_CSS = `
.js-studio {
  --bg:#fff; --ink:#0e1116; --mut:#667085; --line:#e5e7ec; --soft:#f3f4f8;
  --pink:#A6812C; --st1:#fff; --st2:#eef1f6; --bin-bg:#fef2f2; --bin-border:#dc2626;
  color-scheme:light;
  display:grid; grid-template-columns:340px 1fr 340px;
  height:100%; min-height:0; width:100%;
  background:var(--bg); color:var(--ink);
  font:14px/1.4 "Inter", system-ui, sans-serif;
  overscroll-behavior:none;
}
.js-studio *, .js-studio *::before, .js-studio *::after { box-sizing:border-box; }
.js-studio button { font:inherit; color:inherit; background:none; border:0; padding:0; cursor:pointer; }
.js-studio .pal { display:flex; flex-direction:column; min-height:0; border-right:1px solid var(--line); background:var(--bg); }
.js-studio .tabs { display:flex; flex-wrap:wrap; gap:6px; padding:10px 12px; border-bottom:1px solid var(--line); flex-shrink:0; justify-content:center; background:var(--bg); }
.js-studio .tab { padding:8px 12px; border-radius:99px; border:1px solid var(--line); white-space:nowrap; font-weight:500; color:var(--mut); min-height:36px; flex-shrink:0; font-size:13px; background:var(--bg); transition:background .15s, color .15s, border-color .15s; }
.js-studio .tab[aria-pressed=true] { background:var(--ink); color:var(--bg); border-color:var(--ink); }
.js-studio .pb { flex:1; overflow-y:auto; padding:12px; }
.js-studio .pb input[type=search] { width:100%; padding:10px 12px; border:1px solid var(--line); border-radius:10px; background:var(--soft); color:var(--ink); margin-bottom:10px; font:inherit; }
.js-studio .grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(88px,1fr)); gap:8px; }
.js-studio .card { border:1px solid var(--line); border-radius:12px; padding:6px 4px 8px; text-align:center; display:flex; flex-direction:column; align-items:center; gap:2px; background:var(--bg); transition:border-color .15s, transform .1s; }
.js-studio .card:active { transform:scale(.96); }
.js-studio .card .th { width:100%; aspect-ratio:1; border-radius:10px; background:radial-gradient(circle at 35% 30%, var(--st1), var(--st2)); display:grid; place-items:center; overflow:hidden; }
.js-studio .card .th img { width:92%; height:92%; object-fit:contain; }
.js-studio .card .n { font-size:11px; color:var(--mut); max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.js-studio .card .s { font-size:11px; font-weight:700; }
.js-studio .card .s i { font-style:normal; color:var(--pink); margin-right:4px; }
.js-studio .lab { font-size:11px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:var(--mut); margin:12px 0 6px; }
.js-studio .chips { display:flex; flex-wrap:wrap; gap:8px; }
.js-studio .chip { width:32px; height:32px; border-radius:50%; border:2px solid transparent; box-shadow:inset 0 -2px 3px rgba(0,0,0,.25), 0 0 0 1px var(--line); }
.js-studio .chip[aria-pressed=true] { box-shadow:0 0 0 2px var(--bg), 0 0 0 4px var(--ink); }
.js-studio .seg { display:flex; gap:4px; background:var(--soft); border-radius:99px; padding:3px; flex-wrap:wrap; }
.js-studio .seg button { flex:1 1 auto; padding:8px 10px; border-radius:99px; font-weight:600; font-size:12.5px; color:var(--mut); white-space:nowrap; transition:background .15s, color .15s; }
.js-studio .seg button[aria-pressed=true] { background:var(--ink); color:var(--bg); }
.js-studio .main { display:flex; flex-direction:column; min-width:0; min-height:0; position:relative; background:var(--bg); }
.js-studio .stage { position:relative; flex:1; min-height:0; background:radial-gradient(90% 70% at 50% 45%, var(--st1), var(--st2)); }
.js-studio .main-canvas { position:absolute; inset:0; width:100%; height:100%; touch-action:none; cursor:grab; }
.js-studio .top { position:absolute; top:12px; left:12px; right:12px; display:flex; gap:8px; align-items:center; pointer-events:none; flex-wrap:wrap; }
.js-studio .top > * { pointer-events:auto; }
.js-studio .zoom { margin-left:auto; display:flex; gap:6px; }
.js-studio .rb { width:40px; height:40px; border-radius:50%; background:var(--bg); border:1px solid var(--line); font-size:18px; display:grid; place-items:center; }
.js-studio .rb:hover { border-color:var(--pink); }
.js-studio .hint { position:absolute; bottom:10px; left:0; right:0; text-align:center; font-size:11.5px; color:var(--mut); pointer-events:none; }
.js-studio .bin-zone { position:absolute; bottom:24px; right:24px; width:80px; height:80px; border-radius:50%; background:var(--bin-bg); border:2px dashed var(--bin-border); display:grid; place-items:center; font-size:28px; opacity:.6; transition:opacity .2s, transform .2s; pointer-events:none; z-index:5; color:var(--bin-border); }
.js-studio .bin-zone.active { opacity:1; transform:scale(1.15); border-style:solid; }

/* ============================================================
   RIGHT PANEL — Scoop & Stone themed
   ============================================================ */
.js-studio .right-panel {
  display:flex; flex-direction:column;
  padding:24px 22px 28px;
  gap:22px;
  background:#FFFFFF;
  border-left:1px solid #E8E0D8;
  min-width:320px; max-width:380px;
  overflow-y:auto;
  color:#17171A;
}

/* --- Piece picker (Necklace / Bracelet / Anklet) --- */
.js-studio .right-panel .seg.big {
  display:flex; gap:6px;
  background:#FAF8F3;
  border:1px solid #E8E0D8;
  border-radius:99px;
  padding:4px;
  flex-wrap:wrap;
}
.js-studio .right-panel .seg.big button {
  flex:1 0 auto;
  padding:12px 14px;
  border-radius:99px;
  font:700 11px/1 "JetBrains Mono", ui-monospace, monospace;
  letter-spacing:0.18em;
  text-transform:uppercase;
  color:#6B6B63;
  white-space:nowrap;
  background:transparent;
  border:0;
  transition: background .15s, color .15s, box-shadow .15s;
}
.js-studio .right-panel .seg.big button:hover:not([aria-pressed=true]) {
  color:#17171A;
  background:rgba(166,129,44,0.06);
}
.js-studio .right-panel .seg.big button[aria-pressed=true] {
  background:#17171A;
  color:#FFFFFF;
  box-shadow:0 6px 18px rgba(23,23,26,0.15);
}

/* --- Length block --- */
.js-studio .right-panel .len-block {
  display:flex; flex-direction:column; gap:12px;
  padding-top:6px;
  border-top:1px solid #E8E0D8;
}
.js-studio .right-panel .len-block .row {
  display:flex; justify-content:space-between; align-items:baseline;
  padding-top:16px;
}
.js-studio .right-panel .len-block .row > span {
  font:700 10px/1 "JetBrains Mono", ui-monospace, monospace !important;
  letter-spacing:0.28em !important;
  color:#6B6B63 !important;
  text-transform:uppercase !important;
}
.js-studio .right-panel .len-block .row b {
  font:400 32px/1 "Cormorant Garamond", Georgia, serif;
  color:#17171A;
  letter-spacing:-0.01em;
}
.js-studio .right-panel .len-block input[type=range] {
  width:100%;
  height:32px;
  accent-color:#A6812C;
  background:transparent;
  cursor:pointer;
}

/* --- Total block --- */
.js-studio .right-panel .tot-block {
  display:flex; flex-direction:column; align-items:flex-end; gap:4px;
  padding:18px 0 20px;
  border-top:1px solid #E8E0D8;
  border-bottom:1px solid #E8E0D8;
}
.js-studio .right-panel .tot-block .a {
  font:400 44px/1 "Cormorant Garamond", Georgia, serif;
  color:#A6812C;
  letter-spacing:-0.02em;
}
.js-studio .right-panel .tot-block .m {
  font:700 10px/1.4 "JetBrains Mono", ui-monospace, monospace;
  letter-spacing:0.2em;
  text-transform:uppercase;
  color:#6B6B63;
}

/* --- Action buttons --- */
.js-studio .right-panel .act-block {
  display:flex; flex-direction:column; gap:10px;
  margin-top:auto;
}
.js-studio .right-panel .act-block button {
  padding:16px 18px;
  border-radius:999px;
  font:700 11px/1 "JetBrains Mono", ui-monospace, monospace;
  letter-spacing:0.22em;
  text-transform:uppercase;
  min-height:52px;
  background:#FFFFFF;
  color:#17171A;
  border:1px solid #E8E0D8;
  transition: background .15s, color .15s, border-color .15s, transform .08s;
}
.js-studio .right-panel .act-block button:hover:not(:disabled) {
  border-color:#A6812C;
}
.js-studio .right-panel .act-block button:active:not(:disabled) {
  transform:scale(0.98);
}
.js-studio .right-panel .act-block button:disabled {
  opacity:.35;
  cursor:not-allowed;
}

/* Add to cart — gold, primary */
.js-studio .right-panel .act-block .go {
  background:#A6812C;
  color:#FFFFFF;
  border-color:#A6812C;
  box-shadow:0 8px 22px rgba(166,129,44,0.25);
}
.js-studio .right-panel .act-block .go:hover:not(:disabled) {
  background:#8E6E25;
  border-color:#8E6E25;
}

/* Buy now — ink, secondary */
.js-studio .right-panel .act-block button.go[style*="17171A"] {
  background:#17171A !important;
  color:#FFFFFF !important;
  border-color:#17171A !important;
  box-shadow:0 8px 22px rgba(23,23,26,0.18);
}
.js-studio .right-panel .act-block button.go[style*="17171A"]:hover:not(:disabled) {
  background:#000000 !important;
  border-color:#000000 !important;
}

/* Save image — ghost text button */
.js-studio .right-panel .act-block button:last-child {
  background:transparent;
  border-color:transparent;
  color:#6B6B63;
  min-height:44px;
  padding:10px 12px;
}
.js-studio .right-panel .act-block button:last-child:hover:not(:disabled) {
  color:#A6812C;
  background:rgba(166,129,44,0.06);
  border-color:transparent;
}

.js-studio .dock { display:none; }

@media (max-width: 900px) {
  .js-studio { grid-template-columns:1fr; grid-template-rows:minmax(0,1fr) minmax(0,48dvh); }
  .js-studio .main { order:1; }
  .js-studio .pal { order:2; border-right:0; border-top:1px solid var(--line); border-radius:18px 18px 0 0; box-shadow:0 -8px 24px rgba(0,0,0,.08); }
  .js-studio .hint { display:none; }
  .js-studio .right-panel { display:none; }

  /* --- Mobile dock — Scoop & Stone themed --- */
  .js-studio .dock {
    display:grid;
    grid-template-columns:1fr auto;
    gap:10px 16px;
    align-items:center;
    padding:12px 16px;
    border-top:1px solid #E8E0D8;
    background:#FFFFFF;
    flex-shrink:0;
  }
  .js-studio .dock .piece-row {
    grid-column:1/-1;
    display:flex; gap:6px;
    padding-bottom:6px;
    border-bottom:1px solid #E8E0D8;
  }
  .js-studio .dock .piece-row button {
    flex:1;
    padding:10px 8px;
    border-radius:999px;
    border:1px solid #E8E0D8;
    font:700 10px/1 "JetBrains Mono", ui-monospace, monospace;
    letter-spacing:0.18em;
    text-transform:uppercase;
    color:#6B6B63;
    background:#FFFFFF;
    white-space:nowrap;
    transition: background .15s, color .15s;
  }
  .js-studio .dock .piece-row button[aria-pressed=true] {
    background:#17171A; color:#FFFFFF; border-color:#17171A;
  }
  .js-studio .dock .len {
    display:flex; align-items:center; gap:12px;
  }
  .js-studio .dock .len span {
    font:700 9px/1 "JetBrains Mono", ui-monospace, monospace !important;
    letter-spacing:0.22em;
    text-transform:uppercase;
    color:#6B6B63;
  }
  .js-studio .dock .len b {
    font:400 24px/1 "Cormorant Garamond", Georgia, serif;
    color:#17171A;
    min-width:64px;
  }
  .js-studio .dock .len input {
    flex:1; accent-color:#A6812C; height:32px;
  }
  .js-studio .dock .tot {
    text-align:right;
  }
  .js-studio .dock .tot .a {
    font:400 28px/1 "Cormorant Garamond", Georgia, serif;
    color:#A6812C;
  }
  .js-studio .dock .tot .m {
    font:700 9px/1.3 "JetBrains Mono", ui-monospace, monospace;
    letter-spacing:0.18em;
    text-transform:uppercase;
    color:#6B6B63;
  }
  .js-studio .dock .act {
    display:flex; flex-wrap:wrap; gap:8px;
    grid-column:1/-1;
  }
  .js-studio .dock .act button {
    padding:12px 14px;
    border-radius:999px;
    border:1px solid #E8E0D8;
    font:700 10px/1 "JetBrains Mono", ui-monospace, monospace;
    letter-spacing:0.18em;
    text-transform:uppercase;
    background:#FFFFFF;
    color:#17171A;
    min-height:44px;
    flex:1 1 auto;
    transition: background .15s, color .15s, border-color .15s;
  }
  .js-studio .dock .act .go {
    background:#A6812C; color:#FFFFFF; border-color:#A6812C;
    box-shadow:0 6px 16px rgba(166,129,44,0.22);
  }
  .js-studio .dock .act .go:disabled {
    opacity:.35; cursor:not-allowed; box-shadow:none;
  }
  .js-studio .dock .act button:last-child {
    background:transparent; border-color:transparent; color:#6B6B63;
  }
}
`;

let stylesInjected = false;
function ensureStyles() {
  if (stylesInjected) return;
  if (typeof document === "undefined") return;
  if (document.getElementById("js-studio-styles")) { stylesInjected = true; return; }
  const tag = document.createElement("style");
  tag.id = "js-studio-styles";
  tag.textContent = STUDIO_CSS;
  document.head.appendChild(tag);
  stylesInjected = true;
}

/* ============================================================
   HELPERS
   ============================================================ */
const TAU = Math.PI * 2;
const cap = (s: string) => s[0].toUpperCase() + s.slice(1);
const h2 = (h: string) => {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const mix = (a: string, b: string, t: number) => {
  const A = h2(a), B = h2(b);
  return "#" + A.map((v, i) => Math.round(v + (B[i] - v) * t).toString(16).padStart(2, "0")).join("");
};
const rgba = (h: string, a: number) => "rgba(" + h2(h) + "," + a + ")";

const METAL: Record<string, string[]> = {
  silver: ["#ffffff", "#c9ced6", "#6c7482", "#eef1f6", "#7d8593", "#dfe3ea"],
  gold: ["#fff4c2", "#e8c25a", "#8f6212", "#f6d77c", "#80530c", "#ecc866"],
  rose: ["#ffe6dc", "#e3a78f", "#8f5440", "#f4c4b1", "#85493a", "#eab7a3"],
};
const MB: Record<string, string> = { silver: "#c4cad4", gold: "#d4a83c", rose: "#d68c73" };

const EYE: Record<string, string[]> = {
  blue: ["#1e3a8a", "#3b82f6"], pink: ["#be185d", "#ec4899"],
  purple: ["#6b21a8", "#a855f7"], black: ["#111111", "#555555"],
};

const COL: Record<string, string> = {
  blue: "#1e63c4", pink: "#e8709b", red: "#b81c37", orange: "#e8853a",
  yellow: "#e8b734", green: "#1a8a52", mint: "#3ec8b5", lavender: "#a78bfa",
  purple: "#7b3ac9", black: "#1a1d24",
};

const BODIES: any[] = [];
const addBody = (id: string, name: string, cat: string, size: number, price: number, kind: string, o: any = {}) =>
  BODIES.push({ id, name, cat, size, price, kind, ...o });

[6, 8, 10, 12].forEach(s => addBody("pe" + s, `Pearl ${s}mm`, "pearl", s, s * 1.4, "pearl"));
[["Pink", "#f2c6d4"], ["Black", "#3b3b40"], ["Teal", "#9fd8c8"], ["Gold", "#e8d3a2"]]
  .forEach(([n, t]) => addBody("pe" + n, `${n} Pearl`, "pearl", 8, 12, "pearl", { tint: t }));
Object.entries(COL).forEach(([k, h]) =>
  addBody("c" + k, cap(k), "color", 8, 10, "sphere", { hex: h, mat: k === "black" ? "matte" : "plastic" }));
[["silver", "#c4cad4", 5], ["gold", "#d4a83c", 6], ["rose", "#d68c73", 6]]
  .forEach(([k, h, p]) => addBody("m" + k, `${cap(k)} ball`, "metal", 6, p as number, "sphere", { hex: h, mat: "metal" }));
[["silver", "#c4cad4"], ["gold", "#d4a83c"], ["rose", "#d68c73"]]
  .forEach(([k, h]) => addBody("sq" + k, `Square ${cap(k)}`, "metal", 8, 12, "cube", { hex: h, mat: "metal" }));
Object.entries(COL).forEach(([k, h]) =>
  addBody("sqc" + k, `Square ${cap(k)}`, "color", 8, 11, "cube", { hex: h, mat: k === "black" ? "matte" : "plastic" }));
addBody("stTiger", "Tiger Eye", "stone", 10, 28, "stone", { stoneType: "tiger" });
addBody("stMarble", "Marble", "stone", 10, 22, "stone", { stoneType: "marble" });
addBody("stCharcoal", "Charcoal", "stone", 10, 20, "stone", { stoneType: "charcoal" });
addBody("stHematite", "Hematite", "stone", 10, 24, "stone", { stoneType: "hematite" });
addBody("stLava", "Lava", "stone", 10, 26, "stone", { stoneType: "lava" });
[["Red", "#b81c37"], ["Blue", "#1e63c4"], ["Green", "#16a34a"], ["Yellow", "#e8b734"],
 ["Orange", "#e8853a"], ["White", "#f5f5f5"], ["Black", "#1a1a1a"], ["Turquoise", "#3ec8b5"]]
  .forEach(([n, h]) => addBody("seed" + n, `${n} Seed Bead`, "traditional", 4, 3, "seed", { hex: h }));
[["Natural", "#f0e0c8"], ["White", "#faf6ee"], ["Brown", "#a67c52"], ["Black", "#3a2e28"]]
  .forEach(([n, h]) => addBody("cowrie" + n, `${n} Cowrie`, "traditional", 12, 8, "cowrie", { hex: h }));
[["Red", "#e63946"], ["Blue", "#3b82f6"], ["Green", "#2ecc71"], ["Amber", "#f5a623"],
 ["Turquoise", "#1de9b6"], ["Purple", "#b06ef0"]]
  .forEach(([n, h]) => addBody("india" + n, `${n} India Bead`, "traditional", 10, 9, "india", { hex: h }));
[["Clear", "#e4edfa"], ["Blue", "#8db6f2"], ["Pink", "#f4a9c6"], ["Green", "#8fe0b8"],
 ["Amber", "#f6cf7a"], ["Violet", "#bfa2f0"]]
  .forEach(([n, t]) => addBody("f" + n, `${n} Crystal`, "crystal", 8, 15, "facet", { tint: t }));
addBody("fclear10", "Clear Crystal 10mm", "crystal", 10, 22, "facet");
addBody("iced", "Iced-out Ball", "crystal", 10, 34, "iced");
[["Yellow", "#ffd54f"], ["Pink", "#f472b6"], ["Blue", "#60a5fa"]]
  .forEach(([n, h]) => addBody("sm" + n, `${n} Smiley`, "charm", 10, 16, "smiley", { hex: h }));
Object.keys(EYE).forEach(k => addBody("ey" + k, `Evil Eye ${cap(k)}`, "charm", 9, 12, "eye", { eye: k }));
[["Pink", "#f472b6"], ["Blue", "#3b82f6"], ["Red", "#e11d48"]]
  .forEach(([n, h]) => addBody("hr" + n, `${n} Heart`, "charm", 10, 14, "enamel", { shape: "heart", hex: h, hookMetal: "silver" }));
addBody("xs", "Silver Cross", "charm", 11, 25, "cross", { metal: "silver" });
addBody("xg", "Gold Cross", "charm", 11, 28, "cross", { metal: "gold" });
addBody("xr", "Rhinestone Cross", "charm", 12, 35, "cross", { metal: "silver", stones: 1 });
[["round", "Round", 12, 85], ["heart", "Heart", 13, 90], ["drop", "Teardrop", 14, 95],
 ["cross", "Cross", 14, 88], ["star", "Star", 13, 82], ["moon", "Moon", 14, 92]]
  .forEach(([s, n, z, p]) => ["gold", "silver"].forEach(m =>
    addBody("p" + s + m, `${cap(m)} ${n} Diamond`, "pendant", z as number, (p as number) + (m === "gold" ? 6 : 0),
      "pend", { shape: s, metal: m })));
addBody("pGoldPhoto", "Gold Photo Pendant", "pendant", 14, 145, "metal_photo", { metal: "gold" });
addBody("pSilverPhoto", "Silver Photo Pendant", "pendant", 14, 130, "metal_photo", { metal: "silver" });
addBody("pRosePhoto", "Rose Gold Photo Pendant", "pendant", 14, 150, "metal_photo", { metal: "rose" });
addBody("boneTooth", "African Bone Tooth", "pendant", 20, 145, "bonetooth");
addBody("strawberry", "Strawberry", "charm", 10, 18, "enamel", { shape: "strawberry", hex: "#ffffff", hookMetal: "silver" });

const LC = [
  ["White", "#ffffff", "#111111"], ["Black", "#111111", "#ffffff"],
  ["Gold", "#fef08a", "#854d0e"], ["Pink", "#f472b6", "#ffffff"],
  ["Blue", "#60a5fa", "#ffffff"], ["Mint", "#6ee7b7", "#064e3b"],
  ["Orange", "#fb923c", "#ffffff"], ["Purple", "#a855f7", "#ffffff"],
  ["Red", "#ef4444", "#ffffff"],
].map(([n, bg, fg]) => ({ n, bg, fg }));

const STRINGS = [
  ["nylon", "Nylon Cord", "cord", 15, 2.2], ["silk", "Silk Cord", "cord", 25, 2.6],
  ["leather", "Leather", "leather", 35, 3], ["elastic", "Elastic", "cord", 12, 2],
  ["wire", "Wire", "wire", 45, 1.4], ["clear", "Clear", "clear", 10, 2],
  ["chs", "Silver Chain", "chain", 55, 2.4, "silver"], ["chg", "Gold Chain", "chain", 65, 2.4, "gold"],
  ["chr", "Rose Chain", "chain", 62, 2.4, "rose"], ["clasp", "Silver Clasp", "clasp", 70, 2.4, "silver"],
  ["claspg", "Gold Clasp", "clasp", 80, 2.4, "gold"],
].map(a => ({ id: a[0], name: a[1], mat: a[2], price: a[3] as number, th: a[4] as number, metal: a[5] as string | undefined }));

const STRING_COLORS = [
  ["black", "#1a1a1a"], ["white", "#f5f5f5"], ["cream", "#f0e6d2"],
  ["tan", "#c19a6b"], ["brown", "#7c4a10"], ["red", "#b81c37"],
  ["pink", "#ec4899"], ["purple", "#7b3ac9"], ["navy", "#1e3a8a"],
  ["blue", "#3b82f6"], ["teal", "#0f766e"], ["green", "#16a34a"],
  ["mint", "#6ee7b7"], ["yellow", "#fbbf24"], ["orange", "#fb923c"],
  ["silver", "#c4cad4"], ["gold", "#d4a83c"],
];

const PIECE_CONFIG = {
  necklace: { l: "Necklace", min: 35, max: 90, def: 45, c: 2.5 },
  bracelet: { l: "Bracelet", min: 14, max: 24, def: 18, c: 1.5 },
  anklet: { l: "Anklet", min: 20, max: 32, def: 25, c: 1.8 },
};

const TABS = [
  ["all", "All"], ["traditional", "Traditional"], ["pearl", "Pearls"],
  ["color", "Colour"], ["metal", "Metal"], ["stone", "Stones"],
  ["crystal", "Crystal"], ["charm", "Charms"], ["pendant", "Pendants"],
  ["letters", "Letters"], ["string", "String"],
];

const KIND_SCALE: Record<string, number> = { pend: 1.15, cross: 1.1, enamel: 1.05, metal_photo: 1.2, bonetooth: 1.05 };
const HOOK_OFFSET: Record<string, number> = { metal_photo: 0.40, bonetooth: 0.44 };

const isHangingKind = (k: string) =>
  /pend|metal_photo|cross|tooth|bonetooth/.test(k);

const gapFor = (item: any, piece: string) => {
  const isPendant = isHangingKind(item.kind);
  const tight = piece === "bracelet" || piece === "anklet";
  if (tight) return isPendant ? 0.10 : 0.02;
  return isPendant ? 0.50 : 0.55;
};
const sizeForItem = (item: any, piece: string) => {
  if (item.kind === "seed" && piece !== "necklace") return item.size * 0.62;
  return item.size;
};

const deriveMetalType = (beads: any[]): string | undefined => {
  const metals = new Set<string>();
  beads.forEach(b => { if (b.metal) metals.add(b.metal); });
  if (metals.size === 0) return undefined;
  if (metals.size > 1) return "Mixed";
  const m = [...metals][0];
  return m === "rose" ? "Rose Gold" : m[0].toUpperCase() + m.slice(1);
};
const deriveGemstone = (beads: any[]): string | undefined => {
  const kinds = new Set<string>();
  beads.forEach(b => {
    if (b.kind === "pearl") kinds.add("Pearl");
    else if (b.kind === "facet" || b.kind === "iced") kinds.add("Crystal");
    else if (b.kind === "stone") kinds.add("Stone");
    else if (b.kind === "cowrie") kinds.add("Cowrie");
  });
  if (kinds.size === 0) return undefined;
  if (kinds.size > 1) return "Mixed";
  return [...kinds][0];
};

const lg = (g: any, x0: number, y0: number, x1: number, y1: number, st: any[]) => {
  const r = g.createLinearGradient(x0, y0, x1, y1);
  st.forEach((s: any) => r.addColorStop(s[0], s[1]));
  return r;
};
const rg = (g: any, x0: number, y0: number, r0: number, x1: number, y1: number, r1: number, st: any[]) => {
  const r = g.createRadialGradient(x0, y0, r0, x1, y1, r1);
  st.forEach((s: any) => r.addColorStop(s[0], s[1]));
  return r;
};
const circ = (g: any, x: number, y: number, r: number) => {
  g.beginPath(); g.arc(x, y, r, 0, TAU);
};
const rngOf = (str: string) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 16777619); }
  return () => {
    h = (h + 0x6D2B79F5) | 0;
    let t = Math.imul(h ^ (h >>> 15), 1 | h);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

function gloss(g: any, x: number, y: number, r: number, k = 1) {
  const A = (a: number) => "rgba(255,255,255," + (a * k).toFixed(3) + ")";
  g.save(); circ(g, x, y, r * 0.98); g.clip();
  g.fillStyle = rg(g, x, y, r * 0.72, x, y, r, [[0, A(0)], [1, A(0.2)]]);
  g.fillRect(x - r, y - r, 2 * r, 2 * r);
  g.save(); g.filter = `blur(${r * 0.03}px)`;
  g.strokeStyle = A(0.28); g.lineWidth = r * 0.05; g.lineCap = "round";
  g.beginPath(); g.arc(x, y, r * 0.84, 0.25, 1.25); g.stroke();
  g.restore();
  g.save(); g.translate(x - r * 0.34, y - r * 0.38); g.rotate(-0.62); g.scale(1, 0.62);
  g.fillStyle = rg(g, 0, 0, 0, 0, 0, r * 0.55, [[0, A(0.45)], [1, A(0)]]);
  circ(g, 0, 0, r * 0.55); g.fill(); g.restore();
  g.save(); g.translate(x - r * 0.38, y - r * 0.42); g.rotate(-0.62);
  g.filter = `blur(${r * 0.02}px)`;
  const w = r * 0.52, h = r * 0.3;
  g.fillStyle = lg(g, 0, -h / 2, 0, h / 2, [[0, A(0.97)], [1, A(0.6)]]);
  g.beginPath(); g.roundRect(-w / 2, -h / 2, w, h, h * 0.42); g.fill();
  g.strokeStyle = "rgba(150,160,185," + (0.3 * k).toFixed(3) + ")";
  g.lineWidth = r * 0.02;
  g.beginPath(); g.moveTo(0, -h / 2); g.lineTo(0, h / 2);
  g.moveTo(-w / 2, 0); g.lineTo(w / 2, 0); g.stroke();
  g.restore();
  g.fillStyle = A(0.95);
  g.beginPath(); g.ellipse(x + r * 0.42, y + r * 0.36, r * 0.045, r * 0.035, 0.6, 0, TAU); g.fill();
  g.restore();
}

const SH: Record<string, (x: number, y: number, s: number) => Path2D> = {
  round: (x, y, s) => { const p = new Path2D(); p.arc(x, y, s * 0.9, 0, TAU); return p; },
  heart: (x, y, s) => {
    const p = new Path2D();
    p.moveTo(x, y + s * 0.9);
    p.bezierCurveTo(x - s * 1.35, y - s * 0.05, x - s * 0.6, y - s * 1.1, x, y - s * 0.42);
    p.bezierCurveTo(x + s * 0.6, y - s * 1.1, x + s * 1.35, y - s * 0.05, x, y + s * 0.9);
    return p;
  },
  drop: (x, y, s) => {
    const p = new Path2D();
    p.moveTo(x, y - s);
    p.bezierCurveTo(x + s * 0.95, y - s * 0.05, x + s * 0.8, y + s * 0.92, x, y + s * 0.92);
    p.bezierCurveTo(x - s * 0.8, y + s * 0.92, x - s * 0.95, y - s * 0.05, x, y - s);
    return p;
  },
  star: (x, y, s) => {
    const p = new Path2D();
    for (let i = 0; i < 10; i++) {
      const rr = i % 2 ? s * 0.44 : s;
      const a = -Math.PI / 2 + i * Math.PI / 5;
      i ? p.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr)
        : p.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    }
    p.closePath(); return p;
  },
  cross: (x, y, s) => {
    const p = new Path2D();
    const thick = s * 0.32, halfW = s * 0.72, top = -s * 0.98, bottom = s * 0.98;
    const horizY = -s * 0.15, t = thick / 2;
    p.moveTo(x - t, y + top); p.lineTo(x + t, y + top);
    p.lineTo(x + t, y + horizY - t); p.lineTo(x + halfW, y + horizY - t);
    p.lineTo(x + halfW, y + horizY + t); p.lineTo(x + t, y + horizY + t);
    p.lineTo(x + t, y + bottom); p.lineTo(x - t, y + bottom);
    p.lineTo(x - t, y + horizY + t); p.lineTo(x - halfW, y + horizY + t);
    p.lineTo(x - halfW, y + horizY - t); p.lineTo(x - t, y + horizY - t);
    p.closePath(); return p;
  },
  moon: (x, y, s) => {
    const p = new Path2D();
    p.moveTo(x + s * 0.3, y - s * 0.95);
    p.bezierCurveTo(x - s * 1.5, y - s * 0.6, x - s * 1.5, y + s * 0.6, x + s * 0.3, y + s * 0.95);
    p.bezierCurveTo(x - s * 0.6, y + s * 0.5, x - s * 0.6, y - s * 0.5, x + s * 0.3, y - s * 0.95);
    return p;
  },
  strawberry: (x, y, s) => {
    const p = new Path2D();
    p.moveTo(x, y - s * 0.9);
    p.bezierCurveTo(x + s * 1.1, y - s * 0.8, x + s * 0.9, y + s * 0.3, x, y + s * 0.9);
    p.bezierCurveTo(x - s * 0.9, y + s * 0.3, x - s * 1.1, y - s * 0.8, x, y - s * 0.9);
    return p;
  },
};

/* ============================================================
   SPRITE PAINTERS
   ============================================================ */
function stone(g: any, x: number, y: number, r: number, type: string) {
  const sh = rg(g, x, y + r * 0.18, r * 0.3, x, y + r * 0.18, r * 1.25,
    [[0, "rgba(8,12,28,.35)"], [0.55, "rgba(8,12,28,.12)"], [1, "rgba(8,12,28,0)"]]);
  g.fillStyle = sh; circ(g, x, y + r * 0.18, r * 1.25); g.fill();
  if (type === "tiger") {
    g.fillStyle = rg(g, x - r * 0.3, y - r * 0.35, 0, x, y, r * 1.4,
      [[0, "#f5d8a0"], [0.2, "#d4a85c"], [0.4, "#a87830"], [0.6, "#6a4818"], [0.8, "#3a2808"], [1, "#1a1004"]]);
    circ(g, x, y, r); g.fill();
    g.save(); circ(g, x, y, r); g.clip();
    for (let i = 0; i < 14; i++) {
      const yy = y - r + ((i + 0.5) * r * 2) / 14;
      g.fillStyle = i % 2 ? "rgba(255,220,140,.25)" : "rgba(80,45,10,.18)";
      g.beginPath();
      g.moveTo(x - r, yy);
      g.bezierCurveTo(x - r * 0.3, yy - r * 0.18, x + r * 0.3, yy + r * 0.18, x + r, yy);
      g.lineTo(x + r, yy + r * 0.22);
      g.bezierCurveTo(x + r * 0.3, yy + r * 0.4, x - r * 0.3, yy - r * 0.04, x - r, yy + r * 0.22);
      g.closePath(); g.fill();
    }
    g.fillStyle = "rgba(255,240,200,.3)";
    g.beginPath(); g.ellipse(x - r * 0.2, y - r * 0.3, r * 0.7, r * 0.2, -0.3, 0, TAU); g.fill();
    g.restore(); gloss(g, x, y, r, 0.9);
    g.strokeStyle = "rgba(0,0,0,.35)"; g.lineWidth = r * 0.035; circ(g, x, y, r); g.stroke();
  } else if (type === "marble") {
    g.fillStyle = rg(g, x - r * 0.35, y - r * 0.4, 0, x, y, r * 1.3,
      [[0, "#fdfdfd"], [0.3, "#f0f0f5"], [0.6, "#d0d0dc"], [0.85, "#a8a8b8"], [1, "#808090"]]);
    circ(g, x, y, r); g.fill();
    g.save(); circ(g, x, y, r); g.clip();
    g.strokeStyle = "rgba(120,120,140,.5)"; g.lineWidth = r * 0.07; g.lineCap = "round";
    for (let i = 0; i < 7; i++) {
      const sx = x - r + Math.random() * r * 2, sy = y - r + Math.random() * r * 2;
      g.beginPath(); g.moveTo(sx, sy);
      g.bezierCurveTo(sx + r * 0.6, sy + r * 0.3, sx + r * 0.4, sy - r * 0.5, sx + r * 1.4, sy + r * 0.1); g.stroke();
    }
    g.strokeStyle = "rgba(255,255,255,.7)"; g.lineWidth = r * 0.035;
    for (let i = 0; i < 5; i++) {
      const sx = x - r + Math.random() * r * 2, sy = y - r + Math.random() * r * 2;
      g.beginPath(); g.moveTo(sx, sy);
      g.bezierCurveTo(sx + r * 0.5, sy + r * 0.2, sx + r * 0.3, sy - r * 0.3, sx + r * 1.1, sy + r * 0.2); g.stroke();
    }
    g.fillStyle = "rgba(255,255,255,.15)";
    g.beginPath(); g.ellipse(x - r * 0.3, y - r * 0.35, r * 0.5, r * 0.3, -0.4, 0, TAU); g.fill();
    g.restore(); gloss(g, x, y, r, 0.95);
    g.strokeStyle = "rgba(0,0,0,.3)"; g.lineWidth = r * 0.035; circ(g, x, y, r); g.stroke();
  } else if (type === "charcoal") {
    g.fillStyle = rg(g, x - r * 0.35, y - r * 0.4, 0, x, y, r * 1.3,
      [[0, "#7a7a88"], [0.3, "#484858"], [0.6, "#20202c"], [0.9, "#0c0c14"]]);
    circ(g, x, y, r); g.fill();
    g.save(); circ(g, x, y, r); g.clip();
    for (let i = 0; i < 120; i++) {
      const a = i * 2.4, d = Math.sqrt(((i * 37) % 100) / 100) * r;
      g.fillStyle = i % 5 === 0 ? "rgba(255,255,255,.09)" : "rgba(0,0,0,.15)";
      circ(g, x + Math.cos(a) * d, y + Math.sin(a) * d, r * 0.025); g.fill();
    }
    g.restore(); gloss(g, x, y, r, 0.45);
    g.strokeStyle = "rgba(0,0,0,.55)"; g.lineWidth = r * 0.035; circ(g, x, y, r); g.stroke();
  } else if (type === "hematite") {
    g.fillStyle = rg(g, x - r * 0.35, y - r * 0.4, 0, x, y, r * 1.3,
      [[0, "#d0d0e0"], [0.3, "#8888a0"], [0.6, "#505068"], [0.85, "#282838"]]);
    circ(g, x, y, r); g.fill();
    g.save(); circ(g, x, y, r); g.clip();
    g.fillStyle = lg(g, 0, y - r, 0, y + r,
      [[0, "rgba(255,255,255,.7)"], [0.3, "rgba(255,255,255,.1)"], [0.5, "rgba(0,0,0,.5)"],
       [0.7, "rgba(200,200,255,.3)"], [1, "rgba(255,255,255,.6)"]]);
    g.fillRect(x - r, y - r, 2 * r, 2 * r);
    g.fillStyle = lg(g, x - r, 0, x + r, 0,
      [[0, "rgba(255,255,255,.2)"], [0.5, "rgba(255,255,255,0)"], [1, "rgba(255,255,255,.15)"]]);
    g.fillRect(x - r, y - r, 2 * r, 2 * r);
    g.restore(); gloss(g, x, y, r, 0.8);
    g.strokeStyle = "rgba(0,0,0,.45)"; g.lineWidth = r * 0.035; circ(g, x, y, r); g.stroke();
  } else if (type === "lava") {
    g.fillStyle = rg(g, x - r * 0.35, y - r * 0.4, 0, x, y, r * 1.3,
      [[0, "#9a7a68"], [0.3, "#6a4a38"], [0.6, "#3a2218"], [0.9, "#1a0e08"]]);
    circ(g, x, y, r); g.fill();
    g.save(); circ(g, x, y, r); g.clip();
    for (let i = 0; i < 80; i++) {
      const a = i * 2.4, d = Math.sqrt(((i * 53) % 100) / 100) * r;
      const pr = r * 0.04 + Math.random() * r * 0.06;
      g.fillStyle = `rgba(0,0,0,${0.2 + Math.random() * 0.3})`;
      circ(g, x + Math.cos(a) * d, y + Math.sin(a) * d, pr); g.fill();
    }
    for (let i = 0; i < 15; i++) {
      const a = i * 1.8, d = Math.sqrt(((i * 73) % 100) / 100) * r;
      g.fillStyle = "rgba(220,160,100,.15)";
      circ(g, x + Math.cos(a) * d, y + Math.sin(a) * d, r * 0.05); g.fill();
    }
    g.restore(); gloss(g, x, y, r, 0.35);
    g.strokeStyle = "rgba(0,0,0,.6)"; g.lineWidth = r * 0.04; circ(g, x, y, r); g.stroke();
  }
}

function sphere(g: any, x: number, y: number, r: number, base: string, m: string, face?: any, gk?: number) {
  const sh = rg(g, x, y + r * 0.18, r * 0.3, x, y + r * 0.18, r * 1.25,
    [[0, "rgba(8,12,28,.35)"], [0.55, "rgba(8,12,28,.12)"], [1, "rgba(8,12,28,0)"]]);
  g.fillStyle = sh; circ(g, x, y + r * 0.18, r * 1.25); g.fill();
  const P: Record<string, number[]> = {
    plastic: [0.65, 0.22, 0.32, 0.66], metal: [0.8, 0.3, 0.42, 0.8],
    pearl: [0.75, 0.3, 0.3, 0.5], glass: [0.85, 0.3, 0.28, 0.6],
    matte: [0.28, 0.08, 0.22, 0.48],
  };
  const p = P[m] || P.plastic;
  g.fillStyle = rg(g, x - r * 0.38, y - r * 0.42, r * 0.03, x + r * 0.1, y + r * 0.2, r * 1.15,
    [[0, mix(base, "#ffffff", p[0])], [0.22, mix(base, "#ffffff", p[1])], [0.52, base],
     [0.8, mix(base, "#000000", p[2])], [1, mix(base, "#000000", p[3])]]);
  circ(g, x, y, r); g.fill();
  g.save(); circ(g, x, y, r); g.clip();
  if (m === "metal") {
    g.fillStyle = lg(g, 0, y - r, 0, y + r,
      [[0, "rgba(255,255,255,.7)"], [0.4, "rgba(255,255,255,.12)"], [0.5, "rgba(0,0,0,.5)"],
       [0.62, "rgba(0,0,0,.12)"], [1, "rgba(255,225,185,.4)"]]);
    g.fillRect(x - r, y - r, 2 * r, 2 * r);
  }
  if (m === "pearl") {
    g.fillStyle = rg(g, x + r * 0.45, y + r * 0.4, 0, x + r * 0.45, y + r * 0.4, r * 0.9,
      [[0, "rgba(255,170,200,.38)"], [1, "rgba(255,170,200,0)"]]);
    g.fillRect(x - r, y - r, 2 * r, 2 * r);
    g.fillStyle = rg(g, x - r * 0.55, y + r * 0.2, 0, x - r * 0.55, y + r * 0.2, r * 0.8,
      [[0, "rgba(150,230,215,.3)"], [1, "rgba(150,230,215,0)"]]);
    g.fillRect(x - r, y - r, 2 * r, 2 * r);
    for (let i = 0; i < 70; i++) {
      const a = i * 2.4, d = Math.sqrt(((i * 37) % 100) / 100) * r;
      g.fillStyle = "rgba(255,255,255,.07)";
      circ(g, x + Math.cos(a) * d, y + Math.sin(a) * d, r * 0.02); g.fill();
    }
  }
  g.fillStyle = rg(g, x + r * 0.1, y + r * 0.85, 0, x + r * 0.1, y + r * 0.85, r * 0.8,
    [[0, rgba(mix(base, "#ffe9c8", 0.5), 0.38)], [1, "rgba(255,255,255,0)"]]);
  g.fillRect(x - r, y - r, 2 * r, 2 * r);
  g.fillStyle = rg(g, x, y, r * 0.55, x, y, r, [[0, "rgba(0,0,0,0)"], [1, "rgba(0,0,0,.4)"]]);
  g.fillRect(x - r, y - r, 2 * r, 2 * r);
  g.fillStyle = rg(g, x - r * 0.2, y - r * 0.25, r * 0.82, x - r * 0.2, y - r * 0.25, r * 1.12,
    [[0, "rgba(255,255,255,0)"], [1, "rgba(255,255,255,.3)"]]);
  g.fillRect(x - r, y - r, 2 * r, 2 * r);
  g.restore();
  if (face) face(g, x, y, r);
  gloss(g, x, y, r, gk == null ? 1 : gk);
  g.strokeStyle = "rgba(0,0,0,.22)"; g.lineWidth = r * 0.02; circ(g, x, y, r); g.stroke();
}

function cubeBead(g: any, x: number, y: number, r: number, base: string, m: string) {
  const R = rngOf("sq" + base + (m || ""));
  const s = r * 1.55, h = s / 2, rad = r * 0.30;
  const body = new Path2D();
  body.roundRect(x - h, y - h, s, s, rad);
  g.fillStyle = rg(g, x, y + h * 0.9, h * 0.3, x, y + h * 0.9, h * 1.35,
    [[0, "rgba(8,12,28,.38)"], [0.6, "rgba(8,12,28,.12)"], [1, "rgba(8,12,28,0)"]]);
  g.beginPath(); g.ellipse(x, y + h * 0.85, h * 1.05, h * 0.5, 0, 0, TAU); g.fill();
  const metal = m === "metal";
  g.save(); g.fill(body); g.clip(body);
  if (metal) {
    g.fillStyle = lg(g, 0, y - h, 0, y + h,
      [[0, mix(base, "#ffffff", 0.85)], [0.18, mix(base, "#ffffff", 0.45)], [0.46, base],
       [0.60, mix(base, "#000000", 0.40)], [0.78, mix(base, "#000000", 0.18)], [1, mix(base, "#ffffff", 0.40)]]);
    g.fillRect(x - h, y - h, s, s);
    g.fillStyle = lg(g, 0, y - h, 0, y + h * 0.2,
      [[0, "rgba(255,255,255,0)"], [0.55, "rgba(255,255,255,.55)"], [1, "rgba(255,255,255,0)"]]);
    g.fillRect(x - h, y - h, s, s);
  } else {
    g.fillStyle = lg(g, 0, y - h, 0, y + h,
      [[0, mix(base, "#ffffff", 0.65)], [0.18, mix(base, "#ffffff", 0.30)], [0.50, base],
       [0.82, mix(base, "#000000", 0.35)], [0.94, mix(base, "#000000", 0.50)], [1, mix(base, "#ffffff", 0.22)]]);
    g.fillRect(x - h, y - h, s, s);
    g.fillStyle = rg(g, x, y + h * 0.45, 0, x, y + h * 0.45, h * 0.9,
      [[0, rgba(mix(base, "#ffffff", 0.5), 0.32)], [1, rgba(base, 0)]]);
    g.fillRect(x - h, y - h, s, s);
  }
  g.fillStyle = lg(g, x - h, 0, x + h, 0,
    [[0, "rgba(0,0,0,.50)"], [0.16, "rgba(0,0,0,0)"], [0.84, "rgba(0,0,0,0)"], [1, "rgba(0,0,0,.50)"]]);
  g.fillRect(x - h, y - h, s, s);
  const grainCount = metal ? 24 : 40;
  for (let i = 0; i < grainCount; i++) {
    g.fillStyle = R() > 0.5 ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.08)";
    circ(g, x + (R() - 0.5) * s * 0.92, y + (R() - 0.5) * s * 0.92, r * 0.012); g.fill();
  }
  g.save(); g.filter = `blur(${r * 0.03}px)`;
  if (metal) {
    g.fillStyle = "rgba(255,255,255,.95)";
    g.beginPath(); g.roundRect(x - h * 0.72, y - h * 0.72, h * 1.35, h * 0.30, h * 0.12); g.fill();
    g.fillStyle = "rgba(255,255,255,.45)";
    g.beginPath(); g.roundRect(x - h * 0.55, y - h * 0.25, h * 1.05, h * 0.14, h * 0.07); g.fill();
  } else {
    g.fillStyle = "rgba(255,255,255,.92)";
    g.beginPath(); g.roundRect(x - h * 0.72, y - h * 0.74, h * 1.35, h * 0.22, h * 0.11); g.fill();
    g.fillStyle = "rgba(255,255,255,.30)";
    g.beginPath(); g.roundRect(x - h * 0.60, y + h * 0.60, h * 1.20, h * 0.09, h * 0.05); g.fill();
  }
  g.restore();
  g.fillStyle = "rgba(255,255,255,.95)";
  g.beginPath(); g.ellipse(x - h * 0.74, y - h * 0.5, r * 0.045, r * 0.04, 0, 0, TAU); g.fill();
  g.restore();
  g.strokeStyle = "rgba(255,255,255,.55)"; g.lineWidth = r * 0.05;
  g.beginPath(); g.roundRect(x - h + r * 0.05, y - h + r * 0.05, s - r * 0.10, s - r * 0.10, rad * 0.85); g.stroke();
  g.strokeStyle = metal ? "rgba(0,0,0,.42)" : "rgba(0,0,0,.32)"; g.lineWidth = r * 0.04;
  g.beginPath(); g.roundRect(x - h, y - h, s, s, rad); g.stroke();
}

function brilliant(g: any, x: number, y: number, r: number, tint?: string, ns?: number) {
  tint = tint || "#e4edfa";
  if (!ns) {
    const sh = rg(g, x, y + r * 0.12, r * 0.3, x, y + r * 0.12, r * 1.2,
      [[0, "rgba(8,12,28,.3)"], [0.6, "rgba(8,12,28,.1)"], [1, "rgba(8,12,28,0)"]]);
    g.fillStyle = sh; circ(g, x, y + r * 0.12, r * 1.2); g.fill();
  }
  const P = (a: number, k: number) => [x + Math.cos(a) * r * k, y + Math.sin(a) * r * k];
  const T: number[][] = [], M: number[][] = [];
  for (let i = 0; i < 8; i++) {
    T.push(P((i * TAU) / 8, 0.5));
    M.push(P(((i + 0.5) * TAU) / 8, 0.97));
  }
  const dk = mix(tint, "#3a4a6b", 0.7), hi = mix(tint, "#ffffff", 0.9);
  const poly = (pts: number[][], i: number, ang: number) => {
    g.beginPath();
    pts.forEach((p, j) => (j ? g.lineTo(p[0], p[1]) : g.moveTo(p[0], p[1])));
    g.closePath();
    const v = Math.max(0, Math.min(1, (0.5 + 0.5 * Math.cos(ang - 2.3)) * 0.75 + (i % 2) * 0.25));
    g.fillStyle = mix(dk, hi, v); g.fill();
    g.strokeStyle = "rgba(60,80,120,.5)"; g.lineWidth = Math.max(0.5, r * 0.014); g.stroke();
  };
  for (let i = 0; i < 8; i++) {
    const j = (i + 1) % 8;
    poly([T[i], T[j], M[i]], i, ((i + 0.5) * TAU) / 8);
    poly([T[i], M[(i + 7) % 8], M[i]], i + 1, (i * TAU) / 8);
  }
  g.beginPath();
  T.forEach((p, j) => (j ? g.lineTo(p[0], p[1]) : g.moveTo(p[0], p[1])));
  g.closePath();
  g.fillStyle = lg(g, x - r * 0.5, y - r * 0.5, x + r * 0.5, y + r * 0.5,
    [[0, "#ffffff"], [0.5, hi], [1, mix(tint, "#9fb2d4", 0.5)]]);
  g.fill();
  g.strokeStyle = "rgba(60,80,120,.55)"; g.lineWidth = Math.max(0.5, r * 0.014); g.stroke();
  g.strokeStyle = "rgba(255,255,255,.6)"; g.lineWidth = Math.max(0.5, r * 0.012);
  g.beginPath();
  g.moveTo(T[0][0], T[0][1]); g.lineTo(T[4][0], T[4][1]);
  g.moveTo(T[2][0], T[2][1]); g.lineTo(T[6][0], T[6][1]); g.stroke();
  g.save();
  g.strokeStyle = "rgba(255,255,255,.98)"; g.lineCap = "round";
  g.lineWidth = Math.max(0.7, r * 0.035);
  const gx = x - r * 0.22, gy = y - r * 0.26, L = r * 0.4;
  g.beginPath();
  g.moveTo(gx - L, gy); g.lineTo(gx + L, gy);
  g.moveTo(gx, gy - L); g.lineTo(gx, gy + L); g.stroke();
  g.restore();
}

function pave(g: any, path: Path2D, cx: number, cy: number, span: number, sr: number, tint: string, fn?: any) {
  const ok = (a: number, b: number) => g.isPointInPath(path, a, b);
  const dy = sr * 1.5;
  let row = 0;
  for (let yy = cy - span; yy <= cy + span; yy += dy, row++) {
    for (let xx = cx - span + (row % 2) * sr; xx <= cx + span; xx += sr * 2) {
      const q = sr * 0.85;
      if (ok(xx, yy) && ok(xx + q, yy) && ok(xx - q, yy) && ok(xx, yy + q) && ok(xx, yy - q)) {
        (fn || brilliant)(g, xx, yy, sr, tint, 1);
      }
    }
  }
}

function iced(g: any, x: number, y: number, r: number) {
  sphere(g, x, y, r, "#d6dce8", "metal", null, 0);
  const p = new Path2D();
  p.arc(x, y, r * 0.96, 0, TAU);
  pave(g, p, x, y, r, r * 0.13, "#e8f0fc");
  g.save(); circ(g, x, y, r); g.clip();
  g.fillStyle = rg(g, x, y, r * 0.5, x, y, r, [[0, "rgba(0,0,0,0)"], [1, "rgba(10,15,35,.55)"]]);
  g.fillRect(x - r, y - r, 2 * r, 2 * r); g.restore();
  gloss(g, x, y, r, 0.7);
}

function seedBead(g: any, x: number, y: number, r: number, base: string) {
  const R = rngOf("sd" + base);
  const halfW = r * 0.95, halfH = r * 0.72, rad = halfH * 0.62;
  g.fillStyle = rg(g, x, y + halfH * 0.55, halfH * 0.3, x, y + halfH * 0.55, halfW * 1.25,
    [[0, "rgba(8,12,28,.38)"], [0.6, "rgba(8,12,28,.12)"], [1, "rgba(8,12,28,0)"]]);
  g.beginPath(); g.ellipse(x, y + halfH * 0.5, halfW * 1.05, halfH * 1.25, 0, 0, TAU); g.fill();
  const body = new Path2D();
  body.roundRect(x - halfW, y - halfH, halfW * 2, halfH * 2, rad);
  g.save(); g.clip(body);
  g.fillStyle = lg(g, 0, y - halfH, 0, y + halfH,
    [[0, mix(base, "#ffffff", 0.60)], [0.18, mix(base, "#ffffff", 0.28)], [0.50, base],
     [0.82, mix(base, "#000000", 0.35)], [0.94, mix(base, "#000000", 0.50)], [1, mix(base, "#ffffff", 0.22)]]);
  g.fillRect(x - halfW, y - halfH, halfW * 2, halfH * 2);
  g.fillStyle = lg(g, x - halfW, 0, x + halfW, 0,
    [[0, "rgba(0,0,0,.5)"], [0.16, "rgba(0,0,0,0)"], [0.84, "rgba(0,0,0,0)"], [1, "rgba(0,0,0,.5)"]]);
  g.fillRect(x - halfW, y - halfH, halfW * 2, halfH * 2);
  g.fillStyle = rg(g, x, y + halfH * 0.45, 0, x, y + halfH * 0.45, halfW * 0.8,
    [[0, rgba(mix(base, "#ffffff", 0.5), 0.35)], [1, rgba(base, 0)]]);
  g.fillRect(x - halfW, y - halfH, halfW * 2, halfH * 2);
  for (let i = 0; i < 40; i++) {
    g.fillStyle = R() > 0.5 ? "rgba(255,255,255,.07)" : "rgba(0,0,0,.08)";
    circ(g, x + (R() - 0.5) * halfW * 1.8, y + (R() - 0.5) * halfH * 1.8, r * 0.012); g.fill();
  }
  g.save(); g.filter = `blur(${r * 0.025}px)`;
  g.fillStyle = "rgba(255,255,255,.92)";
  g.beginPath(); g.roundRect(x - halfW * 0.72, y - halfH * 0.74, halfW * 1.25, halfH * 0.22, halfH * 0.11); g.fill();
  g.fillStyle = "rgba(255,255,255,.3)";
  g.beginPath(); g.roundRect(x - halfW * 0.6, y + halfH * 0.6, halfW * 1.1, halfH * 0.09, halfH * 0.05); g.fill();
  g.restore();
  g.fillStyle = "rgba(255,255,255,.95)";
  g.beginPath(); g.ellipse(x - halfW * 0.74, y - halfH * 0.5, r * 0.045, r * 0.04, 0, 0, TAU); g.fill();
  g.restore();
  g.strokeStyle = "rgba(0,0,0,.3)"; g.lineWidth = r * 0.04;
  g.beginPath(); g.roundRect(x - halfW, y - halfH, halfW * 2, halfH * 2, rad); g.stroke();
}

function cowrieShell(g: any, x: number, y: number, r: number, base: string) {
  const R = rngOf("cw" + base);
  const rx = r * 0.72, ry = r * 1.15;
  g.fillStyle = rg(g, x, y + ry * 0.35, ry * 0.25, x, y + ry * 0.35, ry * 1.15,
    [[0, "rgba(8,12,28,.38)"], [0.55, "rgba(8,12,28,.12)"], [1, "rgba(8,12,28,0)"]]);
  g.beginPath(); g.ellipse(x, y + ry * 0.35, rx * 1.15, ry * 1.05, 0, 0, TAU); g.fill();
  const bodyPath = new Path2D();
  bodyPath.ellipse(x, y, rx, ry, 0, 0, TAU);
  g.save();
  g.fillStyle = rg(g, x - rx * 0.15, y - ry * 0.2, r * 0.05, x, y, ry * 1.15,
    [[0, "#ffffff"], [0.40, mix(base, "#fff9e6", 0.7)],
     [0.80, mix(base, "#e8d5b5", 0.6)], [1, mix(base, "#c4aa82", 0.5)]]);
  g.fill(bodyPath); g.clip(bodyPath);
  g.fillStyle = rg(g, x, y - ry * 0.35, 0, x, y - ry * 0.35, ry * 0.9,
    [[0, "rgba(255,250,230,.45)"], [1, "rgba(255,250,230,0)"]]);
  g.fillRect(x - rx, y - ry, rx * 2, ry * 2);
  g.strokeStyle = "rgba(160,130,90,.14)"; g.lineWidth = r * 0.012;
  for (let i = 0; i < 6; i++) {
    const t = i / 5, yy = y - ry + t * ry * 2;
    g.beginPath(); g.moveTo(x - rx, yy);
    g.bezierCurveTo(x - rx * 0.3, yy - ry * 0.04, x + rx * 0.3, yy + ry * 0.04, x + rx, yy);
    g.stroke();
  }
  const sw = r * 0.10, topY = y - ry * 0.80, botY = y + ry * 0.80;
  const slitPath = new Path2D();
  slitPath.moveTo(x - sw * 0.1, topY);
  slitPath.bezierCurveTo(x - sw * 2.4, topY + ry * 0.18, x + sw * 0.5, topY + ry * 0.30, x - sw * 1.8, topY + ry * 0.50);
  slitPath.bezierCurveTo(x - sw * 2.6, topY + ry * 0.70, x + sw * 0.4, topY + ry * 0.85, x - sw * 2.2, topY + ry * 1.05);
  slitPath.bezierCurveTo(x - sw * 2.8, topY + ry * 0.25, x + sw * 0.6, topY + ry * 0.20, x - sw * 1.6, botY - ry * 0.05);
  slitPath.bezierCurveTo(x - sw * 1.0, botY - ry * 0.02, x - sw * 0.6, botY + r * 0.04, x, botY + r * 0.06);
  slitPath.lineTo(x + sw * 0.2, botY + r * 0.06);
  slitPath.bezierCurveTo(x + sw * 1.6, botY - ry * 0.05, x - sw * 0.4, botY - ry * 0.20, x + sw * 1.8, botY - ry * 0.35);
  slitPath.bezierCurveTo(x + sw * 2.4, botY - ry * 0.50, x - sw * 0.5, botY - ry * 0.70, x + sw * 2.0, botY - ry * 0.90);
  slitPath.bezierCurveTo(x + sw * 2.6, topY + ry * 0.95, x + sw * 1.0, topY + ry * 0.30, x + sw * 0.6, topY + ry * 0.15);
  slitPath.bezierCurveTo(x + sw * 0.8, topY + r * 0.04, x + sw * 0.2, topY - r * 0.02, x - sw * 0.1, topY);
  slitPath.closePath();
  g.save(); g.translate(r * 0.022, r * 0.022);
  g.strokeStyle = "rgba(70,42,20,.5)"; g.lineWidth = r * 0.05; g.stroke(slitPath); g.restore();
  g.save(); g.translate(-r * 0.018, -r * 0.018);
  g.strokeStyle = "rgba(255,255,255,.5)"; g.lineWidth = r * 0.035; g.stroke(slitPath); g.restore();
  g.fillStyle = lg(g, x - sw * 2.5, 0, x + sw * 2.5, 0, [[0, "#2a1608"], [0.5, "#1a0b03"], [1, "#2a1608"]]);
  g.fill(slitPath);
  const TN = 22;
  for (let i = 0; i < TN; i++) {
    const t = (i + 0.5) / TN, yy = topY + t * (botY - topY);
    const offset = Math.sin(t * Math.PI * 3) * sw * 0.6;
    const halfLen = sw * 0.9 * (0.5 + 0.5 * Math.sin(t * Math.PI));
    g.strokeStyle = "rgba(235,215,185,.55)"; g.lineWidth = r * 0.012;
    g.beginPath(); g.moveTo(x + offset - halfLen, yy); g.lineTo(x + offset + halfLen, yy); g.stroke();
  }
  g.save(); g.filter = `blur(${r * 0.10}px)`;
  g.fillStyle = rg(g, x - rx * 0.55, y - ry * 0.3, 0, x - rx * 0.55, y - ry * 0.3, ry * 0.7,
    [[0, "rgba(255,255,255,.95)"], [1, "rgba(255,255,255,0)"]]);
  g.beginPath(); g.ellipse(x - rx * 0.55, y - ry * 0.3, rx * 0.5, ry * 0.55, -0.15, 0, TAU); g.fill();
  g.restore();
  g.save(); g.filter = `blur(${r * 0.10}px)`;
  g.fillStyle = rg(g, x + rx * 0.55, y - ry * 0.15, 0, x + rx * 0.55, y - ry * 0.15, ry * 0.55,
    [[0, "rgba(255,255,255,.6)"], [1, "rgba(255,255,255,0)"]]);
  g.beginPath(); g.ellipse(x + rx * 0.55, y - ry * 0.15, rx * 0.28, ry * 0.4, 0.15, 0, TAU); g.fill();
  g.restore();
  g.fillStyle = "rgba(255,255,255,.85)";
  g.beginPath(); g.ellipse(x - rx * 0.35, y - ry * 0.6, r * 0.04, r * 0.05, 0, 0, TAU); g.fill();
  g.restore();
  g.strokeStyle = "rgba(160,130,90,.35)"; g.lineWidth = r * 0.022; g.stroke(bodyPath);
}

function indiaBead(g: any, x: number, y: number, r: number, base: string) {
  const R = rngOf("in" + base);
  g.fillStyle = rg(g, x, y + r * 0.18, r * 0.3, x, y + r * 0.18, r * 1.3,
    [[0, "rgba(8,12,28,.4)"], [0.55, "rgba(8,12,28,.13)"], [1, "rgba(8,12,28,0)"]]);
  circ(g, x, y + r * 0.18, r * 1.3); g.fill();
  g.fillStyle = rg(g, x - r * 0.38, y - r * 0.42, r * 0.05, x + r * 0.1, y + r * 0.2, r * 1.15,
    [[0, mix(base, "#ffffff", 0.50)], [0.25, mix(base, "#ffffff", 0.18)], [0.55, base],
     [0.82, mix(base, "#000000", 0.22)], [1, mix(base, "#000000", 0.42)]]);
  circ(g, x, y, r); g.fill();
  g.save(); circ(g, x, y, r); g.clip();
  g.fillStyle = rg(g, x, y, 0, x, y, r * 0.55,
    [[0, rgba(mix(base, "#ffffff", 0.35), 0.22)],
     [0.6, rgba(mix(base, "#ffffff", 0.2), 0.06)], [1, "rgba(255,255,255,0)"]]);
  g.fillRect(x - r, y - r, r * 2, r * 2);
  g.fillStyle = rg(g, x, y, r * 0.15, x, y, r * 0.95,
    [[0, rgba(mix(base, "#ffffff", 0.3), 0.28)],
     [0.55, rgba(mix(base, "#ffffff", 0.15), 0.08)], [1, "rgba(255,255,255,0)"]]);
  g.fillRect(x - r, y - r, r * 2, r * 2);
  g.fillStyle = rg(g, x + r * 0.3, y + r * 0.34, 0, x + r * 0.3, y + r * 0.34, r * 0.85,
    [[0, rgba(mix(base, "#ffffff", 0.5), 0.35)], [0.5, rgba(base, 0)]]);
  g.fillRect(x - r, y - r, r * 2, r * 2);
  g.lineCap = "round";
  for (let i = 0; i < 4; i++) {
    const a0 = R() * TAU;
    g.strokeStyle = rgba(mix(base, i % 2 ? "#ffffff" : "#000000", 0.5), 0.08 + R() * 0.08);
    g.lineWidth = r * (0.04 + R() * 0.05);
    g.beginPath();
    g.arc(x + (R() - 0.5) * r * 0.4, y + (R() - 0.5) * r * 0.4,
      r * (0.35 + R() * 0.4), a0, a0 + 1 + R() * 1.2);
    g.stroke();
  }
  for (let i = 0; i < 3; i++) {
    const a = R() * TAU, d = Math.sqrt(R()) * r * 0.5, br = r * (0.02 + R() * 0.022);
    const bx = x + Math.cos(a) * d, by = y + Math.sin(a) * d;
    g.fillStyle = "rgba(255,255,255,.18)"; circ(g, bx, by, br); g.fill();
    g.strokeStyle = "rgba(255,255,255,.5)"; g.lineWidth = br * 0.4; circ(g, bx, by, br); g.stroke();
  }
  for (let i = 0; i < 50; i++) {
    const a = R() * TAU, d = Math.sqrt(R()) * r;
    g.fillStyle = R() > 0.5 ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.06)";
    circ(g, x + Math.cos(a) * d, y + Math.sin(a) * d, r * 0.012); g.fill();
  }
  g.fillStyle = rg(g, x, y, r * 0.62, x, y, r,
    [[0, "rgba(0,0,0,0)"], [1, rgba(mix(base, "#000000", 0.5), 0.38)]]);
  g.fillRect(x - r, y - r, r * 2, r * 2);
  g.fillStyle = rg(g, x, y, r * 0.8, x, y, r,
    [[0, "rgba(255,255,255,0)"], [0.75, rgba(mix(base, "#ffffff", 0.7), 0.28)],
     [1, rgba(mix(base, "#ffffff", 0.7), 0.5)]]);
  g.fillRect(x - r, y - r, r * 2, r * 2);
  g.restore(); gloss(g, x, y, r, 1);
  g.strokeStyle = "rgba(0,0,0,.24)"; g.lineWidth = r * 0.026; circ(g, x, y, r); g.stroke();
  g.strokeStyle = "rgba(255,255,255,.32)"; g.lineWidth = r * 0.014; circ(g, x, y, r * 0.985); g.stroke();
}

function drawHook(g: any, cx: number, hookY: number, r: number, metal: string) {
  const mt = METAL[metal] || METAL.silver;
  const ringR = r * 0.26;
  g.save();
  g.lineWidth = r * 0.09; g.lineCap = "round";
  g.strokeStyle = lg(g, cx - r * 0.08, hookY, cx + r * 0.08, hookY + ringR * 2, mt.map((c, i) => [i / 5, c]));
  g.beginPath(); g.moveTo(cx, hookY + ringR * 0.5); g.lineTo(cx, hookY + ringR * 2.2); g.stroke();
  g.fillStyle = "rgba(0,0,0,.2)";
  circ(g, cx + r * 0.03, hookY + r * 0.04, ringR * 1.08); g.fill();
  g.lineWidth = r * 0.12;
  g.strokeStyle = lg(g, cx - ringR, hookY - ringR, cx + ringR, hookY + ringR, mt.map((c, i) => [i / 5, c]));
  g.beginPath(); g.arc(cx, hookY, ringR, 0, TAU); g.stroke();
  g.lineWidth = r * 0.03; g.strokeStyle = "rgba(255,255,255,.75)";
  g.beginPath(); g.arc(cx, hookY, ringR, Math.PI * 1.1, Math.PI * 1.75); g.stroke();
  g.lineWidth = r * 0.02; g.strokeStyle = "rgba(0,0,0,.4)";
  g.beginPath(); g.arc(cx, hookY, ringR * 0.55, 0, TAU); g.stroke();
  g.restore();
}

function buildCharmSprite(g: any, x: number, y: number, r: number, shapeFn: any, fill: string, stones: any, hookMetal?: string) {
  drawHook(g, x, y - r * 0.92, r,
    hookMetal || (fill === "#d4a83c" ? "gold" : fill === "#d68c73" ? "rose" : "silver"));
  const bodyY = y + r * 0.18;
  const bR = r * 0.78;
  const path = shapeFn(x, bodyY, bR * 0.95);
  const metal = METAL[fill];
  const sh = rg(g, x, bodyY + bR * 0.2, bR * 0.3, x, bodyY + bR * 0.2, bR * 1.3,
    [[0, "rgba(8,12,28,.35)"], [0.6, "rgba(8,12,28,.1)"], [1, "rgba(8,12,28,0)"]]);
  g.fillStyle = sh; circ(g, x, bodyY + bR * 0.2, bR * 1.3); g.fill();
  g.fillStyle = metal
    ? lg(g, x - bR, bodyY - bR, x + bR, bodyY + bR, metal.map((c, i) => [i / (metal.length - 1), c]))
    : lg(g, x - bR, bodyY - bR, x + bR, bodyY + bR,
        [[0, mix(fill, "#ffffff", 0.55)], [0.5, fill], [1, mix(fill, "#000000", 0.45)]]);
  g.fill(path);
  g.save(); g.clip(path); g.lineWidth = bR * 0.2;
  g.save(); g.translate(bR * 0.04, bR * 0.05);
  g.strokeStyle = "rgba(0,0,0,.28)"; g.stroke(path); g.restore();
  g.save(); g.translate(-bR * 0.03, -bR * 0.04);
  g.strokeStyle = "rgba(255,255,255,.5)"; g.stroke(path); g.restore();
  if (!metal) {
    g.fillStyle = lg(g, x, bodyY - bR, x, bodyY,
      [[0, "rgba(255,255,255,.55)"], [1, "rgba(255,255,255,0)"]]);
    g.fillRect(x - bR, bodyY - bR, 2 * bR, bR);
  }
  g.restore();
  g.strokeStyle = metal ? "rgba(0,0,0,.4)" : (fill === "#ffffff" ? "rgba(0,0,0,.3)" : "#d4a83c");
  g.lineWidth = bR * (metal ? 0.045 : 0.07);
  g.stroke(path);
  if (stones) pave(g, path, x, bodyY, bR * 1.05, bR * 0.16, "#e8f0fc");
}

function paintFlatCharm(g: any, x: number, y: number, r: number, it: any) {
  const shapeFn = SH[it.shape] || SH.round;
  const path = shapeFn(x, y, r * 0.82);
  const fill = it.hex || "#f472b6";
  const metal = METAL[fill];

  const sh = rg(g, x, y + r * 0.18, r * 0.3, x, y + r * 0.18, r * 1.25,
    [[0, "rgba(8,12,28,.35)"], [0.55, "rgba(8,12,28,.12)"], [1, "rgba(8,12,28,0)"]]);
  g.fillStyle = sh; circ(g, x, y + r * 0.18, r * 1.25); g.fill();

  g.fillStyle = metal
    ? lg(g, x - r, y - r, x + r, y + r, metal.map((c, i) => [i / (metal.length - 1), c]))
    : lg(g, x - r, y - r, x + r, y + r,
        [[0, mix(fill, "#ffffff", 0.55)], [0.5, fill], [1, mix(fill, "#000000", 0.45)]]);
  g.fill(path);

  g.save(); g.clip(path); g.lineWidth = r * 0.2;
  g.save(); g.translate(r * 0.04, r * 0.05);
  g.strokeStyle = "rgba(0,0,0,.28)"; g.stroke(path); g.restore();
  g.save(); g.translate(-r * 0.03, -r * 0.04);
  g.strokeStyle = "rgba(255,255,255,.5)"; g.stroke(path); g.restore();
  if (!metal) {
    g.fillStyle = lg(g, x, y - r, x, y,
      [[0, "rgba(255,255,255,.55)"], [1, "rgba(255,255,255,0)"]]);
    g.fillRect(x - r, y - r, 2 * r, r);
  }
  g.restore();

  g.strokeStyle = metal ? "rgba(0,0,0,.4)" : (fill === "#ffffff" ? "rgba(0,0,0,.3)" : "#d4a83c");
  g.lineWidth = r * (metal ? 0.045 : 0.07);
  g.stroke(path);

  gloss(g, x, y, r * 0.82, 0.9);
}

function boneToothPendant(g: any, x: number, y: number, r: number) {
  const R = rngOf("bonetooth");
  const scale = (r * 2.9) / 340;
  const sx = (s: number) => x + (s - 100) * scale;
  const sy = (t: number) => y + (t - 170) * scale;
  const tooth = new Path2D();
  tooth.moveTo(sx(100), sy(72));
  tooth.bezierCurveTo(sx(118), sy(74), sx(128), sy(92), sx(130), sy(120));
  tooth.bezierCurveTo(sx(132), sy(155), sx(128), sy(195), sx(118), sy(230));
  tooth.bezierCurveTo(sx(110), sy(262), sx(96), sy(290), sx(78), sy(306));
  tooth.bezierCurveTo(sx(75), sy(308), sx(72), sy(305), sx(74), sy(300));
  tooth.bezierCurveTo(sx(88), sy(278), sx(94), sy(250), sx(96), sy(220));
  tooth.bezierCurveTo(sx(98), sy(185), sx(96), sy(145), sx(90), sy(110));
  tooth.bezierCurveTo(sx(86), sy(90), sx(84), sy(78), sx(100), sy(72));
  tooth.closePath();
  g.save();
  g.translate(scale * 2, scale * 4);
  g.filter = `blur(${r * 0.10}px)`;
  g.fillStyle = "rgba(59,36,16,.32)";
  g.fill(tooth);
  g.restore();
  g.save();
  g.clip(tooth);
  g.fillStyle = rg(g, sx(80), sy(95), 0, sx(100), sy(170), r * 1.9,
    [[0, "#fffdf5"], [0.22, "#fbf4e2"], [0.48, "#f0e4c8"],
     [0.72, "#d8c49c"], [0.90, "#a88d62"], [1, "#6b5434"]]);
  g.fillRect(x - r * 1.4, y - r * 1.6, r * 2.8, r * 3.2);
  g.fillStyle = rg(g, sx(110), sy(150), 0, sx(110), sy(150), r * 1.05,
    [[0, "rgba(255,242,207,.55)"], [0.60, "rgba(232,211,164,.20)"], [1, "rgba(168,137,94,0)"]]);
  g.fillRect(x - r * 1.4, y - r * 1.6, r * 2.8, r * 3.2);
  g.fillStyle = lg(g, 0, sy(0), 0, sy(340),
    [[0, "rgba(138,106,58,0)"], [0.55, "rgba(138,106,58,.05)"],
     [0.82, "rgba(107,74,32,.28)"], [1, "rgba(59,34,8,.60)"]]);
  g.fillRect(x - r * 1.4, y - r * 1.6, r * 2.8, r * 3.2);
  g.fillStyle = lg(g, sx(0), 0, sx(200), 0,
    [[0, "rgba(74,53,32,.35)"], [0.18, "rgba(74,53,32,0)"],
     [0.82, "rgba(74,53,32,0)"], [1, "rgba(74,53,32,.40)"]]);
  g.fillRect(x - r * 1.4, y - r * 1.6, r * 2.8, r * 3.2);
  g.strokeStyle = "rgba(107,74,32,.30)"; g.lineCap = "round";
  const lines = [
    [88, 90, 84, 140, 84, 200, 92, 260, 0.8], [96, 82, 94, 140, 94, 210, 100, 272, 0.6],
    [104, 80, 104, 140, 106, 220, 108, 290, 0.7], [116, 84, 118, 140, 120, 200, 116, 250, 0.6],
    [124, 92, 128, 140, 130, 190, 126, 236, 0.5],
  ];
  for (const [x0, y0, x1, y1, x2, y2, x3, y3, lw] of lines) {
    g.lineWidth = (lw as number) * scale * 1.8;
    g.beginPath(); g.moveTo(sx(x0 as number), sy(y0 as number));
    g.bezierCurveTo(sx(x1 as number), sy(y1 as number), sx(x2 as number), sy(y2 as number), sx(x3 as number), sy(y3 as number));
    g.stroke();
  }
  for (let i = 0; i < 110; i++) {
    const gx = sx(60 + R() * 80), gy = sy(70 + R() * 240), gr = r * (0.005 + R() * 0.009);
    g.fillStyle = "rgba(138,106,58," + (0.06 + R() * 0.11).toFixed(2) + ")";
    circ(g, gx, gy, gr); g.fill();
  }
  g.fillStyle = rg(g, sx(100), sy(68), 0, sx(100), sy(68), r * 0.32,
    [[0, "rgba(59,36,16,.35)"], [1, "rgba(59,36,16,0)"]]);
  g.beginPath(); g.ellipse(sx(100), sy(68), r * 0.30, r * 0.10, 0, 0, TAU); g.fill();
  g.save(); g.filter = `blur(${r * 0.14}px)`;
  g.fillStyle = "rgba(255,255,255,.85)";
  g.beginPath();
  g.moveTo(sx(88), sy(100));
  g.bezierCurveTo(sx(92), sy(88), sx(102), sy(86), sx(102), sy(86));
  g.bezierCurveTo(sx(94), sy(115), sx(92), sy(165), sx(92), sy(165));
  g.bezierCurveTo(sx(88), sy(130), sx(88), sy(100), sx(88), sy(100));
  g.closePath(); g.fill();
  g.restore();
  g.restore();
  g.strokeStyle = "rgba(138,111,72,.45)";
  g.lineWidth = Math.max(0.6, r * 0.008);
  g.stroke(tooth);
  const hx = sx(100), hy = sy(78), hrx = 4.5 * scale, hry = 5.5 * scale;
  g.fillStyle = rg(g, hx - hrx * 0.1, hy - hry * 0.3, 0, hx, hy, hry * 1.2,
    [[0, "#000000"], [0.55, "#150a03"], [1, "#3b2410"]]);
  g.beginPath(); g.ellipse(hx, hy, hrx, hry, 0, 0, TAU); g.fill();
  g.save();
  g.strokeStyle = lg(g, sx(91), sy(52), sx(109), sy(76),
    [[0, "#f5f5f5"], [0.25, "#c8c8c8"], [0.5, "#6a6a6a"], [0.72, "#a0a0a0"], [1, "#3a3a3a"]]);
  g.lineWidth = 3.2 * scale; g.lineCap = "round";
  g.beginPath();
  g.moveTo(sx(95.5), sy(76));
  g.bezierCurveTo(sx(91), sy(68), sx(91), sy(56), sx(100), sy(52));
  g.bezierCurveTo(sx(109), sy(56), sx(109), sy(68), sx(104.5), sy(76));
  g.stroke();
  g.restore();
}

function metalPhotoPendant(g: any, x: number, y: number, r: number, metal: string, uid: string, userPhotos: Map<string, HTMLImageElement>) {
  drawHook(g, x, y - r * 0.92, r, metal);
  const bodyY = y + r * 0.15;
  const bR = r * 0.85;
  const sh = rg(g, x, bodyY + bR * 0.2, bR * 0.3, x, bodyY + bR * 0.2, bR * 1.3,
    [[0, "rgba(8,12,28,.35)"], [0.6, "rgba(8,12,28,.1)"], [1, "rgba(8,12,28,0)"]]);
  g.fillStyle = sh; circ(g, x, bodyY + bR * 0.2, bR * 1.3); g.fill();
  const mt = METAL[metal];
  g.fillStyle = rg(g, x - bR * 0.35, bodyY - bR * 0.4, bR * 0.05, x + bR * 0.1, bodyY + bR * 0.2, bR * 1.15,
    [[0, mt[0]], [0.22, mt[1]], [0.52, mt[3]], [0.78, mt[2]], [1, mt[4]]]);
  circ(g, x, bodyY, bR); g.fill();
  g.save(); circ(g, x, bodyY, bR); g.clip();
  g.fillStyle = lg(g, 0, bodyY - bR, 0, bodyY + bR,
    [[0, "rgba(255,255,255,.65)"], [0.42, "rgba(255,255,255,.08)"],
     [0.52, "rgba(0,0,0,.45)"], [0.66, "rgba(0,0,0,.1)"], [1, "rgba(255,225,185,.4)"]]);
  g.fillRect(x - bR, bodyY - bR, bR * 2, bR * 2);
  g.restore();
  const winR = bR * 0.52;
  g.fillStyle = "rgba(0,0,0,.24)";
  circ(g, x, bodyY + bR * 0.03, winR * 1.06); g.fill();
  const photo = userPhotos.get(uid);
  g.save();
  circ(g, x, bodyY, winR); g.clip();
  if (photo) {
    const iw = photo.width, ih = photo.height, s = Math.min(iw, ih);
    const sx0 = (iw - s) / 2, sy0 = (ih - s) / 2;
    g.drawImage(photo, sx0, sy0, s, s, x - winR, bodyY - winR, winR * 2, winR * 2);
    g.fillStyle = rg(g, x - winR * 0.3, bodyY - winR * 0.35, 0,
      x - winR * 0.3, bodyY - winR * 0.35, winR * 0.9,
      [[0, "rgba(255,255,255,.35)"], [1, "rgba(255,255,255,0)"]]);
    g.fillRect(x - winR, bodyY - winR, winR * 2, winR * 2);
  } else {
    g.fillStyle = "rgba(245,242,235,.92)";
    g.fillRect(x - winR, bodyY - winR, winR * 2, winR * 2);
    g.strokeStyle = "rgba(120,110,90,.55)"; g.lineWidth = winR * 0.08; g.lineCap = "round";
    g.beginPath(); g.roundRect(x - winR * 0.42, bodyY - winR * 0.34, winR * 0.84, winR * 0.68, winR * 0.1); g.stroke();
    g.beginPath(); g.arc(x - winR * 0.18, bodyY - winR * 0.12, winR * 0.10, 0, TAU); g.stroke();
    g.beginPath();
    g.moveTo(x - winR * 0.42, bodyY + winR * 0.26);
    g.lineTo(x - winR * 0.05, bodyY - winR * 0.06);
    g.lineTo(x + winR * 0.18, bodyY + winR * 0.14);
    g.lineTo(x + winR * 0.42, bodyY + winR * 0.26);
    g.stroke();
  }
  g.restore();
  g.strokeStyle = "rgba(0,0,0,.4)"; g.lineWidth = winR * 0.06;
  circ(g, x, bodyY, winR); g.stroke();
  g.strokeStyle = "rgba(255,255,255,.78)"; g.lineWidth = winR * 0.04;
  circ(g, x, bodyY, winR * 1.045); g.stroke();
  gloss(g, x, bodyY, bR, 0.85);
  g.strokeStyle = "rgba(0,0,0,.35)"; g.lineWidth = bR * 0.035;
  circ(g, x, bodyY, bR); g.stroke();
}

function paintBead(g: any, x: number, y: number, r: number, it: any, userPhotos: Map<string, HTMLImageElement>) {
  switch (it.kind) {
    case "pearl": sphere(g, x, y, r, it.tint || "#f2e8dc", "pearl", null, 0.8); break;
    case "sphere": sphere(g, x, y, r, it.hex, it.mat); break;
    case "cube": cubeBead(g, x, y, r, it.hex, it.mat); break;
    case "stone": stone(g, x, y, r, it.stoneType); break;
    case "seed": seedBead(g, x, y, r, it.hex); break;
    case "cowrie": cowrieShell(g, x, y, r, it.hex); break;
    case "india": indiaBead(g, x, y, r, it.hex); break;
    case "facet": brilliant(g, x, y, r, it.tint); break;
    case "iced": iced(g, x, y, r); break;
    case "smiley":
      sphere(g, x, y, r, it.hex, "plastic", (g: any, x: number, y: number, r: number) => {
        g.fillStyle = "#111";
        [-1, 1].forEach(s => { circ(g, x + s * r * 0.33, y - r * 0.12, r * 0.13); g.fill(); });
        g.strokeStyle = "#111"; g.lineCap = "round"; g.lineWidth = r * 0.13;
        g.beginPath(); g.arc(x, y + r * 0.12, r * 0.47, 0.2, Math.PI - 0.2); g.stroke();
      });
      break;
    case "eye": {
      const [o, m] = EYE[it.eye];
      sphere(g, x, y, r, "#f4f8ff", "glass", (g: any, x: number, y: number, r: number) => {
        [[0.76, o], [0.6, m], [0.4, "#ffffff"], [0.19, "#0b1220"]]
          .forEach(([k, c]) => { g.fillStyle = c as string; circ(g, x, y, r * (k as number)); g.fill(); });
        g.strokeStyle = "rgba(0,0,0,.25)"; g.lineWidth = r * 0.02;
        circ(g, x, y, r * 0.76); g.stroke();
      });
      break;
    }
    case "enamel":
      paintFlatCharm(g, x, y, r, it);
      break;
    case "cross": buildCharmSprite(g, x, y, r, SH.cross, it.metal, it.stones, it.metal); break;
    case "pend": buildCharmSprite(g, x, y, r, SH[it.shape], it.metal, 1, it.metal); break;
    case "metal_photo": metalPhotoPendant(g, x, y, r, it.metal, it.uid, userPhotos); break;
    case "bonetooth": boneToothPendant(g, x, y - r * 0.10, r * 1.30); break;
    case "letter": {
      const lc = it.lc;
      if (it.shape === "square") {
        cubeBead(g, x, y, r, lc.bg, "plastic");
        g.font = `700 ${r}px 'Inter',sans-serif`;
        g.textAlign = "center"; g.textBaseline = "middle";
        g.fillStyle = "rgba(0,0,0,.3)"; g.fillText(it.letter, x, y + r * 0.13);
        g.fillStyle = lc.fg; g.fillText(it.letter, x, y + r * 0.07);
        break;
      }
      sphere(g, x, y, r, lc.bg, "glass", null, 0.9);
      g.font = `700 ${r * 1.05}px 'Inter',sans-serif`;
      g.textAlign = "center"; g.textBaseline = "middle";
      g.fillStyle = "rgba(0,0,0,.3)"; g.fillText(it.letter, x, y + r * 0.13);
      g.fillStyle = lc.fg; g.fillText(it.letter, x, y + r * 0.07);
      break;
    }
    default: sphere(g, x, y, r, "#c3c9d2", "plastic");
  }
}

const SP = 320;
const spriteCache = new Map<string, HTMLCanvasElement>();
function sprite(it: any, userPhotos: Map<string, HTMLImageElement>): HTMLCanvasElement {
  if (it.kind === "metal_photo") {
    const c = document.createElement("canvas");
    c.width = c.height = SP;
    paintBead(c.getContext("2d"), SP / 2, SP / 2, SP * 0.4, it, userPhotos);
    return c;
  }
  let c = spriteCache.get(it.id);
  if (!c) {
    c = document.createElement("canvas");
    c.width = c.height = SP;
    paintBead(c.getContext("2d"), SP / 2, SP / 2, SP * 0.4, it, userPhotos);
    spriteCache.set(it.id, c);
  }
  return c;
}

function geo(W: number, H: number, piece: string, lenCm: number, zoom: number) {
  const L = lenCm * 10;
  const isNecklace = piece === "necklace";
  const ovalX = isNecklace ? 0.62 : 1.0;
  const ovalY = 1.0;
  const margin = 0.82;
  let R: number;
  if (isNecklace) {
    const maxByH = (H * margin) / (2 * ovalY);
    const maxByW = (W * margin) / (2 * ovalX);
    R = Math.min(maxByH, maxByW) * zoom;
  } else {
    R = Math.min(W, H) * 0.42 * zoom;
  }
  const ppm = (R * TAU) / L;
  return { ppm, R, cx: W / 2, cy: H / 2, L, ovalX, ovalY, isNecklace };
}
function pointAt(G: any, s: number) {
  const f = s / G.R;
  const x = G.cx + G.R * Math.sin(f) * G.ovalX;
  const y = G.cy + G.R * Math.cos(f) * G.ovalY;
  const dx = G.R * Math.cos(f) * G.ovalX;
  const dy = -G.R * Math.sin(f) * G.ovalY;
  return { x, y, a: Math.atan2(dy, dx), f };
}

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
export const JewelleryStudio: React.FC<JewelleryStudioProps> = ({
  initialPiece = "necklace",
  onSave,
  onAddToCart,
  onBuyNow,
}) => {
  useEffect(() => { ensureStyles(); }, []);

  const [piece, setPieceRaw] = useState<"necklace" | "bracelet" | "anklet">(initialPiece);
  const [len, setLen] = useState(PIECE_CONFIG[initialPiece].def);
  const [placed, setPlaced] = useState<any[]>([]);
  const [hist, setHist] = useState<any[][]>([]);
  const [str, setStr] = useState("nylon");
  const [col, setCol] = useState("black");
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [lshape, setLshape] = useState<"round" | "square">("round");
  const [lc, setLc] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState(0);
  const [toastMsg, setToastMsg] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [pendingAnim, setPendingAnim] = useState<{ id: string; startTime: number } | null>(null);

  const toastTimer = useRef<any>(null);
  const userPhotos = useRef(new Map<string, HTMLImageElement>());
  const pendingPhoto = useRef<{ uid: string | null; dataURL: string | null }>({ uid: null, dataURL: null });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const binRef = useRef<HTMLDivElement>(null);
  const WRef = useRef(600);
  const HRef = useRef(600);
  const posRef = useRef<{ x: number; y: number; d: number; i: number }[]>([]);
  const dragRef = useRef<any>({
    pd: null, draggingIdx: -1, dragStart: null,
    dragBeadIdx: -1, dragBeadTargetIdx: -1,
    dragBeadIsReorder: false, dragBeadLastSwap: -1,
  });

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToastMsg(""), 1600);
  }, []);

  const snap = useCallback(() => {
    setHist(h => {
      const nh = [...h, placed.slice()];
      if (nh.length > 40) nh.shift();
      return nh;
    });
  }, [placed]);

  const setPiece = useCallback((p: "necklace" | "bracelet" | "anklet") => {
    const c = PIECE_CONFIG[p];
    setPieceRaw(p);
    setLen(c.def);
    setPlaced([]);
    setHist([]);
    setOff(0);
    setZoom(1);
  }, []);

  const addItem = useCallback((it: any) => {
    const sz = sizeForItem(it, piece);
    let t = 0;
    placed.forEach((p, i) => {
      const ps = sizeForItem(p, piece);
      t += ps;
      if (i < placed.length - 1)
        t += ((ps + sizeForItem(placed[i + 1], piece)) / 2) * gapFor(p, piece);
    });
    let newTotal = t + sz;
    if (placed.length > 0) {
      newTotal += ((sizeForItem(placed[placed.length - 1], piece) + sz) / 2) *
        gapFor(placed[placed.length - 1], piece);
    }
    if (newTotal > len * 10 - 1) { toast("Piece is full — increase the length"); return; }
    snap();
    const newItem = { ...it, uid: (it.id || "x") + "_" + Math.random().toString(36).slice(2, 9) };
    setPlaced(prev => [...prev, newItem]);
    setPendingAnim({ id: newItem.id, startTime: performance.now() });
    toast(it.name + " added");
    if (newItem.kind === "metal_photo") {
      setTimeout(() => {
        pendingPhoto.current.uid = newItem.uid;
        pendingPhoto.current.dataURL = null;
        setUploadOpen(true);
      }, 220);
    }
  }, [placed, piece, len, snap, toast]);

  const removeItem = useCallback((i: number) => {
    setHist(h => {
      const nh = [...h, placed.slice()];
      if (nh.length > 40) nh.shift();
      return nh;
    });
    const removed = placed[i];
    if (removed) userPhotos.current.delete(removed.uid);
    setPlaced(prev => prev.filter((_, j) => j !== i));
    if (removed) toast(removed.name + " removed");
  }, [placed, toast]);

  const reorder = useCallback((from: number, to: number) => {
    if (from === to) return;
    setHist(h => {
      const nh = [...h, placed.slice()];
      if (nh.length > 40) nh.shift();
      return nh;
    });
    setPlaced(prev => {
      const arr = prev.slice();
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });
  }, [placed]);

  const undo = useCallback(() => {
    setHist(h => {
      if (!h.length) return h;
      const nh = h.slice();
      const prev = nh.pop()!;
      setPlaced(prev);
      return nh;
    });
  }, []);

  const clearAll = useCallback(() => {
    setHist(h => {
      const nh = [...h, placed.slice()];
      if (nh.length > 40) nh.shift();
      return nh;
    });
    setPlaced([]);
    userPhotos.current.clear();
  }, [placed]);

  const zoomBy = useCallback((d: number) => setZoom(z => Math.max(0.6, Math.min(2.4, z + d))), []);

  const letterItem = useCallback((l: string) => {
    const lcObj = LC[lc];
    return {
      id: "L" + l + lc + lshape, kind: "letter", letter: l, lc: lcObj,
      shape: lshape, size: 8, price: 10, name: "Letter " + l,
    };
  }, [lc, lshape]);

  const buildDesign = useCallback((): StudioDesignPayload => {
    let total = 0;
    placed.forEach(p => { total += p.price; });
    total += len * PIECE_CONFIG[piece].c;
    total += STRINGS.find(s => s.id === str)?.price || 0;

    let preview = "";
    try {
      const cv = canvasRef.current;
      if (cv) {
        const side = Math.min(cv.width, cv.height);
        const sx = (cv.width - side) / 2;
        const sy = (cv.height - side) / 2;

        const out = document.createElement("canvas");
        out.width = side;
        out.height = side;
        const octx = out.getContext("2d");
        if (octx) {
          octx.fillStyle = "#f6f3ee";
          octx.fillRect(0, 0, side, side);
          octx.drawImage(cv, sx, sy, side, side, 0, 0, side, side);
          preview = out.toDataURL("image/png");
        } else {
          preview = cv.toDataURL("image/png");
        }
      }
    } catch { /* noop */ }

    return {
      piece,
      length: len,
      string: STRINGS.find(s => s.id === str)?.name || str,
      stringColor: col,
      beads: placed.map(p => ({
        id: p.id, name: p.name, kind: p.kind, size: p.size,
        price: p.price, hex: p.hex, metal: p.metal, shape: p.shape, uid: p.uid,
      })),
      totalPrice: total,
      previewDataURL: preview,
      metalType: deriveMetalType(placed),
      gemstone: deriveGemstone(placed),
    };
  }, [placed, len, piece, str, col]);

  const handleAddToCartClick = useCallback(() => {
    if (!placed.length) { toast("Add at least one bead to continue"); return; }
    const design = buildDesign();
    onSave?.(design);
    onAddToCart?.(design);
  }, [placed.length, onSave, onAddToCart, buildDesign, toast]);

  const handleBuyNowClick = useCallback(() => {
    if (!placed.length) { toast("Add at least one bead to continue"); return; }
    const design = buildDesign();
    onSave?.(design);
    onBuyNow?.(design);
  }, [placed.length, onSave, onBuyNow, buildDesign, toast]);

  const handleSaveImageClick = useCallback(() => {
    if (!placed.length) { toast("Add at least one bead to save"); return; }
    const design = buildDesign();
    try {
      const a = document.createElement("a");
      a.href = design.previewDataURL;
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.download = `scoop-and-stone-${design.piece}-${stamp}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast("Image saved 📸");
    } catch (e) {
      console.error("Save image failed:", e);
      toast("Couldn't save image");
    }
  }, [placed.length, buildDesign, toast]);

  const drawMain = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    const W = WRef.current, H = HRef.current;
    ctx.clearRect(0, 0, W, H);
    const G = geo(W, H, piece, len, zoom);
    drawStringCanvas(ctx, G, str, col, W, H);

    const pos: any[] = [];
    let T = 0;
    placed.forEach((p, i) => {
      const sz = sizeForItem(p, piece);
      T += sz * G.ppm;
      if (i < placed.length - 1) {
        const avg = (sz + sizeForItem(placed[i + 1], piece)) / 2;
        T += avg * G.ppm * gapFor(p, piece);
      }
    });
    const lim = Math.max(0, (G.L * G.ppm - T) / 2);
    const clampedOff = Math.max(-lim, Math.min(lim, off));
    if (clampedOff !== off) setOff(clampedOff);

    let s = -T / 2 + clampedOff;
    const drag = dragRef.current;
    placed.forEach((p, i) => {
      const sz = sizeForItem(p, piece);
      const d = sz * G.ppm;
      const q = pointAt(G, s + d / 2);
      s += d;
      if (i < placed.length - 1) {
        const avg = (sz + sizeForItem(placed[i + 1], piece)) / 2;
        s += avg * G.ppm * gapFor(p, piece);
      }
      const D = d * (KIND_SCALE[p.kind] || 1) / 0.8;
      pos.push({ x: q.x, y: q.y, d, i });
      if (q.x < -D * 3 || q.x > W + D * 3 || q.y < -D * 3 || q.y > H + D * 3) return;

      const isDragged = drag.dragBeadIdx === i && drag.dragBeadIsReorder;
      const isSwapTarget = drag.dragBeadTargetIdx === i && drag.dragBeadIsReorder &&
        drag.dragBeadTargetIdx !== drag.dragBeadIdx;

      ctx.save();
      ctx.translate(q.x, q.y);
      if (isHangingKind(p.kind)) {
        const nx = q.x - G.cx, ny = q.y - G.cy;
        const nlen = Math.hypot(nx, ny) || 1;
        ctx.rotate(Math.atan2(ny / nlen, nx / nlen) - Math.PI / 2);
        const hookOffset = (HOOK_OFFSET[p.kind] ?? 0.46) * D;
        ctx.translate(0, hookOffset);
      } else {
        ctx.rotate(q.a);
      }

      let scale = 1, alpha = 1;
      if (pendingAnim && pendingAnim.id === p.id && pendingAnim.startTime) {
        const elapsed = performance.now() - pendingAnim.startTime;
        const t = Math.min(1, elapsed / 400);
        const c1 = 1.70158, c3 = c1 + 1;
        const easeOut = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
        scale = 0.2 + 0.8 * easeOut;
        alpha = Math.min(1, t * 2.5);
        if (t >= 1) setPendingAnim(null);
        else requestAnimationFrame(drawMain);
      }
      if (isDragged) scale *= 1.18;
      if (isSwapTarget) scale *= 1.08;

      ctx.globalAlpha = alpha;
      ctx.translate(-D / 2, -D / 2);
      ctx.scale(scale, scale);
      ctx.drawImage(sprite(p, userPhotos.current), 0, 0, D, D);
      ctx.restore();

      if (isSwapTarget) {
        ctx.save();
        ctx.strokeStyle = "rgba(166,129,44,.9)";
        ctx.lineWidth = 3;
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.arc(q.x, q.y, D * 0.55, 0, TAU);
        ctx.stroke();
        ctx.restore();
      }
    });
    posRef.current = pos;
  }, [piece, len, zoom, str, col, placed, off, pendingAnim]);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const fit = () => {
      const r = cv.getBoundingClientRect();
      const d = Math.min(window.devicePixelRatio || 1, 3);
      WRef.current = r.width;
      HRef.current = r.height;
      cv.width = r.width * d;
      cv.height = r.height * d;
      cv.getContext("2d")?.setTransform(d, 0, 0, d, 0, 0);
      drawMain();
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(cv);
    return () => ro.disconnect();
  }, [drawMain]);

  useEffect(() => { drawMain(); }, [drawMain]);

  const hitBead = (clientX: number, clientY: number) => {
    const cv = canvasRef.current!;
    const r = cv.getBoundingClientRect();
    const x = clientX - r.left, y = clientY - r.top;
    for (let i = posRef.current.length - 1; i >= 0; i--) {
      const q = posRef.current[i];
      if (Math.hypot(q.x - x, q.y - y) < q.d / 2 + 6) return q.i;
    }
    return -1;
  };
  const overBin = (clientX: number, clientY: number) => {
    const b = binRef.current;
    if (!b) return false;
    const r = b.getBoundingClientRect();
    return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  };
  const findTarget = (clientX: number, clientY: number) => {
    const cv = canvasRef.current!;
    const r = cv.getBoundingClientRect();
    const x = clientX - r.left, y = clientY - r.top;
    let best = -1, bestDist = Infinity;
    posRef.current.forEach((q, i) => {
      const dist = Math.hypot(q.x - x, q.y - y);
      if (dist < bestDist) { bestDist = dist; best = i; }
    });
    return bestDist < 70 ? best : -1;
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const idx = hitBead(e.clientX, e.clientY);
    const drag = dragRef.current;
    if (idx >= 0) {
      drag.draggingIdx = idx;
      drag.dragStart = { x: e.clientX, y: e.clientY };
      drag.dragBeadIdx = idx;
      drag.dragBeadTargetIdx = idx;
      drag.dragBeadIsReorder = false;
      drag.dragBeadLastSwap = -1;
      binRef.current?.classList.add("active");
      e.currentTarget.setPointerCapture(e.pointerId);
      e.currentTarget.style.cursor = "grabbing";
    } else {
      drag.pd = { x: e.clientX, y: e.clientY, lx: e.clientX };
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };
  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (drag.draggingIdx >= 0) {
      const dx = e.clientX - drag.dragStart.x, dy = e.clientY - drag.dragStart.y;
      const dist = Math.hypot(dx, dy);
      if (dist > 12 && !overBin(e.clientX, e.clientY)) drag.dragBeadIsReorder = true;
      if (drag.dragBeadIsReorder) {
        const target = findTarget(e.clientX, e.clientY);
        if (target >= 0 && target !== drag.dragBeadIdx && target !== drag.dragBeadLastSwap) {
          reorder(drag.dragBeadIdx, target);
          drag.dragBeadLastSwap = target;
          drag.dragBeadIdx = target;
          drag.dragBeadTargetIdx = target;
        } else {
          drag.dragBeadTargetIdx = target >= 0 ? target : drag.dragBeadIdx;
        }
        drawMain();
      }
      if (binRef.current) binRef.current.style.opacity = overBin(e.clientX, e.clientY) ? "1" : "0.6";
      return;
    }
    if (!drag.pd) return;
    setOff((o: number) => o + (e.clientX - drag.pd.lx));
    drag.pd.lx = e.clientX;
  };
  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = dragRef.current;
    if (drag.draggingIdx >= 0) {
      const wasReorder = drag.dragBeadIsReorder;
      const idx = drag.draggingIdx;
      drag.draggingIdx = -1;
      binRef.current?.classList.remove("active");
      if (binRef.current) binRef.current.style.opacity = "0.6";
      e.currentTarget.style.cursor = "grab";
      if (wasReorder) toast("Beads reordered");
      else {
        const moved = drag.dragStart &&
          Math.hypot(e.clientX - drag.dragStart.x, e.clientY - drag.dragStart.y) > 6;
        if (overBin(e.clientX, e.clientY) || !moved) removeItem(idx);
      }
      drag.dragBeadIdx = -1;
      drag.dragBeadTargetIdx = -1;
      drag.dragBeadIsReorder = false;
      drag.dragBeadLastSwap = -1;
      drag.pd = null;
      return;
    }
    if (!drag.pd) return;
    if (Math.hypot(e.clientX - drag.pd.x, e.clientY - drag.pd.y) < 6) {
      const cv = canvasRef.current!;
      const r = cv.getBoundingClientRect();
      const x = e.clientX - r.left, y = e.clientY - r.top;
      for (let i = posRef.current.length - 1; i >= 0; i--) {
        const q = posRef.current[i];
        if (Math.hypot(q.x - x, q.y - y) < q.d / 2 + 4) { removeItem(q.i); break; }
      }
    }
    drag.pd = null;
  };

  let beadSum = 0, beadMm = 0;
  placed.forEach(p => { beadSum += p.price; beadMm += p.size; });
  const st = STRINGS.find(s => s.id === str)!;
  const total = beadSum + len * PIECE_CONFIG[piece].c + st.price;
  const canSave = placed.length > 0;

  return (
    <div className="js-studio">
      <aside className="pal">
        <div className="tabs">
          {TABS.map(([k, n]) => (
            <button key={k} className="tab" aria-pressed={tab === k} onClick={() => setTab(k)}>{n}</button>
          ))}
        </div>
        <div className="pb">
          {tab === "string" && (
            <>
              <div className="grid">
                {STRINGS.map(s => (
                  <StringCard key={s.id} s={s} str={str} setStr={setStr} col={col} />
                ))}
              </div>
              <div className="lab">String colour</div>
              <div className="chips">
                {STRING_COLORS.map(c => (
                  <button key={c[0]} className="chip" style={{ background: c[1] }}
                    title={cap(c[0])} aria-pressed={col === c[0]} onClick={() => setCol(c[0])} />
                ))}
              </div>
            </>
          )}
          {tab === "letters" && (
            <>
              <div className="seg">
                <button aria-pressed={lshape === "round"} onClick={() => setLshape("round")}>● Round</button>
                <button aria-pressed={lshape === "square"} onClick={() => setLshape("square")}>■ Square</button>
              </div>
              <div className="lab">Bead colour</div>
              <div className="chips">
                {LC.map((c, i) => (
                  <button key={c.n} className="chip" style={{ background: c.bg }}
                    title={c.n} aria-pressed={lc === i} onClick={() => setLc(i)} />
                ))}
              </div>
              <div className="lab">Tap a letter to add it</div>
              <div className="grid">
                {"ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789".split("").map(l => (
                  <PaletteCard key={l} item={letterItem(l)} onAdd={addItem} userPhotos={userPhotos} />
                ))}
              </div>
            </>
          )}
          {tab !== "string" && tab !== "letters" && (
            <>
              <input type="search" placeholder="Search beads…" value={q} onChange={e => setQ(e.target.value)} />
              <div className="grid">
                {BODIES
                  .filter(b => (tab === "all" || b.cat === tab) && (!q || b.name.toLowerCase().includes(q.toLowerCase())))
                  .map(b => <PaletteCard key={b.id} item={b} onAdd={addItem} userPhotos={userPhotos} />)}
              </div>
            </>
          )}
        </div>
      </aside>

      <main className="main">
        <div className="stage">
          <canvas
            ref={canvasRef}
            className="main-canvas"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onWheel={e => { e.preventDefault(); zoomBy(e.deltaY < 0 ? 0.1 : -0.1); }}
          />
          <div className="top">
            <div className="zoom">
              <button className="rb" onClick={() => zoomBy(-0.1)} aria-label="Zoom out">−</button>
              <button className="rb" onClick={() => zoomBy(0.1)} aria-label="Zoom in">+</button>
            </div>
          </div>
          <div className="hint">Drag bead along chain to reorder · Drag to bin to remove · Tap bead to remove</div>
          <div className="bin-zone" ref={binRef}><span>🗑️</span></div>
        </div>

        <div className="dock">
          <div className="piece-row">
            {Object.entries(PIECE_CONFIG).map(([k, c]) => (
              <button key={k} aria-pressed={piece === k} onClick={() => setPiece(k as any)}>{c.l}</button>
            ))}
          </div>
          <div className="len">
            <span>Length</span>
            <b>{len} cm</b>
            <input type="range" min={PIECE_CONFIG[piece].min} max={PIECE_CONFIG[piece].max} step=".5"
              value={len} onChange={e => setLen(parseFloat(e.target.value))} />
          </div>
          <div className="tot">
            <div className="a">R {total.toFixed(2)}</div>
            <div className="m">{placed.length} items · {(beadMm / 10).toFixed(1)} cm</div>
          </div>
          <div className="act">
            <button onClick={undo}>Undo</button>
            <button onClick={clearAll}>Clear</button>
            <button className="go" onClick={handleAddToCartClick} disabled={!canSave}>
              Add to cart
            </button>
            <button
              className="go"
              onClick={handleBuyNowClick}
              disabled={!canSave}
              style={{ background: "#17171A", borderColor: "#17171A" }}
            >
              Buy now
            </button>
            <button onClick={handleSaveImageClick} disabled={!canSave}>
              Save image
            </button>
          </div>
        </div>
      </main>

      <aside className="right-panel">
        <div className="seg big">
          {Object.entries(PIECE_CONFIG).map(([k, c]) => (
            <button key={k} aria-pressed={piece === k} onClick={() => setPiece(k as any)}>{c.l}</button>
          ))}
        </div>
        <div className="len-block">
          <div className="row">
            <span>Length</span>
            <b>{len} cm</b>
          </div>
          <input type="range" min={PIECE_CONFIG[piece].min} max={PIECE_CONFIG[piece].max} step=".5"
            value={len} onChange={e => setLen(parseFloat(e.target.value))} />
        </div>
        <div className="tot-block">
          <div className="a">R {total.toFixed(2)}</div>
          <div className="m">{placed.length} items · {(beadMm / 10).toFixed(1)} cm of beads</div>
        </div>
        <div className="act-block">
          <button onClick={undo}>Undo</button>
          <button onClick={clearAll}>Clear</button>
          <button className="go" onClick={handleAddToCartClick} disabled={!canSave}>
            Add to cart
          </button>
          <button
            className="go"
            onClick={handleBuyNowClick}
            disabled={!canSave}
            style={{ background: "#17171A", borderColor: "#17171A" }}
          >
            Buy now
          </button>
          <button onClick={handleSaveImageClick} disabled={!canSave}>
            Save image
          </button>
        </div>
      </aside>

      {toastMsg && <div className="toast" style={{ opacity: 1 }}>{toastMsg}</div>}
      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onConfirm={(img) => {
            if (pendingPhoto.current.uid && img) userPhotos.current.set(pendingPhoto.current.uid, img);
            setUploadOpen(false);
            drawMain();
          }}
        />
      )}
    </div>
  );
};

/* ============================================================
   SUBCOMPONENTS
   ============================================================ */
function PaletteCard({ item, onAdd, userPhotos }: any) {
  const url = React.useMemo(
    () => sprite(item, userPhotos.current).toDataURL(),
    [item, userPhotos]
  );
  return (
    <button className="card" title={item.name} onClick={() => onAdd(item)}>
      <div className="th"><img src={url} alt={item.name} /></div>
      <div className="n">{item.name}</div>
      <div className="s"><i>{item.size}mm</i>R{item.price.toFixed(0)}</div>
    </button>
  );
}

function StringCard({ s, str, setStr, col }: any) {
  const url = React.useMemo(() => {
    const c = document.createElement("canvas");
    c.width = c.height = 80;
    const x = c.getContext("2d")!;
    x.lineCap = "round"; x.lineWidth = 7;
    x.strokeStyle = s.mat === "chain" || s.mat === "clasp" ? MB[s.metal]
      : s.mat === "clear" ? "#cbd5e1"
      : STRING_COLORS.find(k => k[0] === col)![1];
    x.beginPath(); x.moveTo(8, 40); x.lineTo(72, 40); x.stroke();
    x.strokeStyle = "rgba(255,255,255,.6)"; x.lineWidth = 1.6;
    x.beginPath(); x.moveTo(8, 38); x.lineTo(72, 38); x.stroke();
    return c.toDataURL();
  }, [s, col]);
  return (
    <button className="card" aria-pressed={str === s.id} onClick={() => setStr(s.id)}
      style={str === s.id ? { borderColor: "var(--ink)" } : undefined}>
      <div className="th"><img src={url} alt={s.name} /></div>
      <div className="n">{s.name}</div>
      <div className="s">R{s.price}</div>
    </button>
  );
}

function UploadModal({ onClose, onConfirm }: any) {
  const [dataURL, setDataURL] = useState<string | null>(null);
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => setDataURL(ev.target?.result as string);
    reader.readAsDataURL(f);
  };
  const handleConfirm = () => {
    if (!dataURL) { onClose(); return; }
    const img = new Image();
    img.onload = () => onConfirm(img);
    img.onerror = () => onConfirm(null);
    img.src = dataURL;
  };
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,.55)",
      display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#fff", borderRadius: 18, padding: 22, maxWidth: 360, width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
        <h3 style={{ margin: "0 0 8px", font: "500 20px 'Cormorant Garamond', Georgia, serif" }}>Add your photo</h3>
        <p style={{ margin: "0 0 16px", fontSize: 13, color: "#667085", lineHeight: 1.5 }}>
          Choose a square photo. It will be centred inside the pendant.
        </p>
        <input type="file" accept="image/*" onChange={handleFile}
          style={{ display: "block", width: "100%", padding: 14, border: "1px dashed #e5e7ec",
            borderRadius: 12, background: "#f3f4f8", marginBottom: 14 }} />
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 12, borderRadius: 12,
            border: "1px solid #e5e7ec", fontWeight: 600, background: "#fff" }}>Cancel</button>
          <button onClick={handleConfirm} style={{ flex: 1, padding: 12, borderRadius: 12,
            border: "1px solid #A6812C", fontWeight: 600, background: "#A6812C", color: "#fff" }}>
            Place photo
          </button>
        </div>
      </div>
    </div>
  );
}

function drawStringCanvas(ctx: any, G: any, str: string, col: string, W: number, H: number) {
  const st = STRINGS.find(s => s.id === str)!;
  const colHex = STRING_COLORS.find(c => c[0] === col)![1];
  const { ppm, R, cx, cy, ovalX, ovalY } = G;
  const w = Math.max(2.5, st.th * ppm * 0.5);
  ctx.save();
  ctx.lineCap = "round";
  if (st.mat === "chain" || st.mat === "clasp") {
    const step = ppm * 3;
    const n = Math.floor((TAU * R) / step);
    const mt = METAL[st.metal!];
    for (let i = 0; i < n; i++) {
      const p = pointAt(G, i * step - (n * step) / 2);
      if (p.x < -60 || p.x > W + 60 || p.y < -60 || p.y > H + 60) continue;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.a);
      const rx = i % 2 ? step * 0.5 : step * 0.85, ry = step * 0.36;
      ctx.lineWidth = ppm * 0.75;
      ctx.strokeStyle = lg(ctx, -rx, -ry, rx, ry, mt.map((c, j) => [j / 5, c]));
      ctx.beginPath(); ctx.ellipse(0, 0, rx, ry, 0, 0, TAU); ctx.stroke();
      ctx.lineWidth = ppm * 0.18;
      ctx.strokeStyle = "rgba(255,255,255,.7)";
      ctx.beginPath(); ctx.ellipse(0, -ppm * 0.12, rx, ry, 0, Math.PI * 1.05, Math.PI * 1.8); ctx.stroke();
      ctx.restore();
    }
  } else {
    const pa = () => { ctx.beginPath(); ctx.ellipse(cx, cy, R * ovalX, R * ovalY, 0, 0, TAU); };
    if (st.mat === "clear") {
      ctx.strokeStyle = "rgba(190,205,225,.7)"; ctx.lineWidth = w; pa(); ctx.stroke();
      ctx.strokeStyle = "rgba(255,255,255,.8)"; ctx.lineWidth = w * 0.3; pa(); ctx.stroke();
    } else {
      ctx.translate(1, w * 0.35);
      ctx.strokeStyle = "rgba(0,0,0,.2)"; ctx.lineWidth = w; pa(); ctx.stroke();
      ctx.translate(-1, -w * 0.35);
      ctx.strokeStyle = colHex; ctx.lineWidth = st.mat === "leather" ? w * 1.3 : w; pa(); ctx.stroke();
      ctx.strokeStyle = "rgba(255,255,255,.4)"; ctx.lineWidth = w * 0.25;
      ctx.save(); ctx.translate(0, -w * 0.22); pa(); ctx.stroke(); ctx.restore();
      if (st.mat === "cord") {
        ctx.setLineDash([w * 0.5, w * 0.5]);
        ctx.strokeStyle = "rgba(0,0,0,.14)"; ctx.lineWidth = w;
        pa(); ctx.stroke();
      }
    }
  }
  ctx.restore();
}

export default JewelleryStudio;